import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  getTasks, getTaskFilterOptions, createTask, updateTask, deleteTask, getStaff, getClients,
  addExpense, deleteExpense, getProfitCentres, getCategories, getBillingEntities,
  getTaskTemplates, createTaskTemplate, updateTaskTemplate, deleteTaskTemplate,
  bulkUpdateTasks, confirmTaskArchive, freezeTask, unfreezeTask,
  getAllSubTasks,
  getSubTasks, createSubTask as createSubTaskApi, updateSubTask as updateSubTaskApi,
  closeSubTask as closeSubTaskApi, deleteSubTask as deleteSubTaskApi,
  getTaskComments, createTaskComment, deleteTaskComment,
  addTaskStaff, addTaskTeam, removeTaskStaff,
  getCertificates as getCertificatesApi, createCertificate as createCertificateApi,
  updateCertificate as updateCertificateApi, deleteCertificate as deleteCertificateApi,
  getComplianceTypes as getComplianceTypesApi,
  getTaskEnrollments, enrollComplianceType, removeEnrollment,
  getTaskCompliances, updateCompliance as updateComplianceApi,
  deleteCompliance as deleteComplianceApi,
  getClientGstins, getClientProfile,
  revealTaskArchiveLink, uploadsUrl,
} from '../api';
import { api } from '../api';
import { showToast } from '../utils/toast';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import CertificatesPage from './Certificates';
import LitigationsPanel from '../components/LitigationsPanel';
import LitigationsList from './Litigations';
import CompliancesPage from './Compliances';
import ColumnFilter, { FilterOption } from '../components/ColumnFilter';
import { useIsMobile } from '../hooks/useIsMobile';
import MobileTasks from './mobile/MobileTasks';
import DateRangeFilter from '../components/DateRangeFilter';

interface Staff { id: number; staffName: string; isPartner?: boolean; isActive?: boolean; reportingPartnerId?: number; }
interface Client { id: number; clientCode: string; clientName: string; }
interface Master { id: number; name: string; }
interface Expense { id: number; description: string; amount: number; date: string; category: string; receiptUrl?: string; }
interface Document { id: number; fileName: string; originalName: string; fileSize: number; mimeType: string; createdAt: string; uploadedBy?: any; }
interface SubTask {
  id: number; subTaskNumber: string; name: string; description?: string;
  assignedTo?: Staff; dueDate?: string;
  status: 'OPEN' | 'SENT_FOR_REVIEW' | 'CLOSED';
  createdAt: string;
}
interface TaskStaffEntry { id: number; staffId: number; staff: { id: number; staffName: string }; }
interface Task {
  id: number; taskId: string; taskName: string;
  partner?: Staff; manager?: Staff; client?: Client;
  profitCentre?: Master; category?: Master; billingEntity?: Master;
  billedAmount?: number;
  status: 'OPEN' | 'CLOSED'; billingStatus: 'BILLED' | 'UNBILLED';
  billDetails?: string; costIncurred?: number; opeIncurred?: number;
  isOverdue?: boolean; dueDate?: string; createdAt?: string;
  archiveLink?: string; archiveLinkSet?: boolean; archivingConfirmed?: boolean;
  isFrozen?: boolean;
  reference?: string; terms?: string;
  expenses?: Expense[]; documents?: any[]; _count?: { documents: number };
  assignedStaff?: TaskStaffEntry[];
  reimbursements?: Array<{
    id: number; claimNumber: string; notes?: string; approvedAt?: string;
    staff: { id: number; staffName: string };
    items: Array<{ id: number; description: string; amount: number; date: string }>;
  }>;
}

const EXPENSE_CATEGORIES = ['TRAVEL', 'COURIER', 'FILING_FEES', 'PRINTING', 'FOOD', 'ACCOMMODATION', 'OTHER'];

const defaultCreateForm = {
  taskName: '', partnerId: '', managerId: '',
  clientId: '', categoryId: '', dueDate: '',
};

const defaultEditForm = {
  taskName: '', partnerId: '', managerId: '',
  clientId: '', profitCentreId: '', categoryId: '', billedAmount: '',
  billingEntityId: '', status: 'OPEN', billingStatus: 'UNBILLED', billDetails: '',
  dueDate: '', archiveLink: '', archivingConfirmed: false as boolean,
  reference: '', terms: '',
};

const fmt = (n: number) => '₹' + n.toLocaleString('en-IN', { maximumFractionDigits: 0 });

const StatusDot: React.FC<{ status: 'OPEN' | 'CLOSED'; billing: 'BILLED' | 'UNBILLED' }> = ({ status, billing }) => (
  <div className="flex items-center gap-1.5" title={`${status} · ${billing}`}>
    <span className={`inline-block w-2.5 h-2.5 rounded-full ${status === 'OPEN' ? 'bg-green-500' : 'bg-gray-400'}`} title={status} />
    <span className={`inline-block w-2.5 h-2.5 rounded-full border-2 ${billing === 'BILLED' ? 'bg-blue-500 border-blue-500' : 'bg-white border-gray-400'}`} title={billing} />
  </div>
);

const TasksContent: React.FC<{ openTaskId?: number; openDetailTab?: 'info' | 'expenses' | 'documents' | 'subtasks' | 'certificates' | 'litigations' | 'compliances' | 'comments' | 'history' }> = ({ openTaskId, openDetailTab }) => {
  const { user, isAdmin, isHR, isPartner } = useAuth();
  const navigate = useNavigate();

  // Per-task permission helpers
  const canSeeCostForTask = (task: Task) =>
    isAdmin ||
    user?.staffId === task.partner?.id ||
    (task.partner as any)?.reportingPartnerId === user?.staffId;

  const canSeeBillingForTask = (task: Task) =>
    isAdmin || isHR ||
    user?.staffId === task.partner?.id ||
    user?.staffId === task.manager?.id ||
    (task.partner as any)?.reportingPartnerId === user?.staffId;

  const canSeeRefTermsForTask = (task: Task) => canSeeCostForTask(task);

  const canUpdateTask = (task: Task) =>
    isAdmin || isHR ||
    user?.staffId === task.partner?.id ||
    user?.staffId === task.manager?.id ||
    (task.partner as any)?.reportingPartnerId === user?.staffId;

  // Any staff member assigned to the task can drop in the archive link, even
  // if they can't edit anything else. Matches the relaxed permission on the
  // server's updateTask handler.
  const canSetArchiveLinkForTask = (task: Task) =>
    canUpdateTask(task) ||
    (task.assignedStaff?.some((a) => a.staffId === user?.staffId) ?? false);

  const canDeleteTask = (_task: Task) => isAdmin || isPartner;

  const canConfirmArchiving = (task: Task) =>
    isAdmin || user?.staffId === task.manager?.id;

  const isCACertification = (task: Task) => task.category?.name === 'CA Certification';

  // Column-level visibility (for header rendering)
  const canSeeCostColumn = isAdmin || isPartner;
  const canSeeBillingColumn = isAdmin || isHR || isPartner;

  const [tasks, setTasks] = useState<Task[]>([]);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [profitCentres, setProfitCentres] = useState<Master[]>([]);
  const [categories, setCategories] = useState<Master[]>([]);
  const [billingEntities, setBillingEntities] = useState<Master[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Task | null>(null);
  const [createForm, setCreateForm] = useState(defaultCreateForm);
  const [editForm, setEditForm] = useState(defaultEditForm);
  // Guards the task create/edit submit against a fast repeat click — without
  // this, each click fires its own request before the first response closes
  // the modal, silently creating N identical tasks (reported 12-Aug-26: four
  // presses of Create produced four identical certificates the same way).
  const [taskSaving, setTaskSaving] = useState(false);
  // Same double-submit guard as taskSaving/certSaving, one flag per
  // independent form below (sub-task, compliance enrollment, template,
  // expense) so saving one doesn't block the others.
  const [subTaskSaving, setSubTaskSaving] = useState(false);
  const [enrollSaving, setEnrollSaving] = useState(false);
  const [templateSaving, setTemplateSaving] = useState(false);
  const [expenseSaving, setExpenseSaving] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterBilling, setFilterBilling] = useState('');
  const [filterClientName, setFilterClientName] = useState('');
  const [filterCategoryName, setFilterCategoryName] = useState('');
  const [filterPartnerName, setFilterPartnerName] = useState('');
  const [filterManagerName, setFilterManagerName] = useState('');
  const [filterBillingEntity, setFilterBillingEntity] = useState('');
  const [filterDueDateFrom, setFilterDueDateFrom] = useState('');
  const [filterDueDateTo, setFilterDueDateTo] = useState('');
  const [filterCreatedFrom, setFilterCreatedFrom] = useState('');
  const [filterCreatedTo, setFilterCreatedTo] = useState('');
  const [filterArchive, setFilterArchive] = useState(''); // '' | 'CONFIRMED' | 'PENDING' | 'NONE'
  const [filterTaskStatus, setFilterTaskStatus] = useState(''); // '' | 'OPEN' | 'CLOSED' | 'FROZEN'
  const [showOverdueOnly, setShowOverdueOnly] = useState(false);
  const [contentSearchParams] = useSearchParams();

  // Apply filter from URL query params on mount (e.g. /tasks?status=OPEN&filter=overdue)
  useEffect(() => {
    const status = contentSearchParams.get('status');
    const filter = contentSearchParams.get('filter');
    if (status) setFilterStatus(status);
    if (filter === 'overdue') setShowOverdueOnly(true);
    // eslint-disable-next-line
  }, []);
  const [sortField, setSortField] = useState<'taskId' | 'taskName' | 'client' | 'dueDate' | 'billedAmount' | 'category' | 'partner' | 'manager' | 'createdAt' | 'status' | 'billingEntity' | 'costIncurred' | 'opeIncurred' | 'archive'>('taskId');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  // Server-side pagination: `tasks` holds only the current page's rows;
  // `total` is the filtered row count across all pages.
  const [total, setTotal] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [refreshTick, setRefreshTick] = useState(0);
  const [filterOptions, setFilterOptions] = useState<{
    clients: string[]; categories: string[]; partners: string[]; managers: string[]; billingEntities: string[];
  }>({ clients: [], categories: [], partners: [], managers: [], billingEntities: [] });
  const [detailTask, setDetailTask] = useState<Task | null>(null);
  const [expenseForm, setExpenseForm] = useState({ description: '', amount: '', date: format(new Date(), 'yyyy-MM-dd'), category: 'OTHER' });
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // Inline edit state: { taskId, field, value }
  const [inlineEdit, setInlineEdit] = useState<{ taskId: number; field: string; value: string } | null>(null);

  // Client search
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState<Client[]>([]);
  const [clientDropOpen, setClientDropOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);

  // Task Templates
  interface TaskTemplate { id: number; name: string; description?: string; categoryId?: number; checklist: string[]; }
  const [templates, setTemplates] = useState<TaskTemplate[]>([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<TaskTemplate | null>(null);
  const [templateForm, setTemplateForm] = useState({ name: '', description: '', categoryId: '', checklist: '' });
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  // Sub-tasks
  const [detailTab, setDetailTab] = useState<'info' | 'expenses' | 'documents' | 'subtasks' | 'certificates' | 'litigations' | 'compliances' | 'comments' | 'history'>('info');
  const [subTasks, setSubTasks] = useState<SubTask[]>([]);
  const [subTasksLoading, setSubTasksLoading] = useState(false);
  const [showSubTaskForm, setShowSubTaskForm] = useState(false);
  const [editingSubTask, setEditingSubTask] = useState<SubTask | null>(null);
  const [subTaskForm, setSubTaskForm] = useState({ name: '', description: '', assignedToId: '', dueDate: '' });

  // Certificates
  interface Certificate {
    id: number; certificateNumber: string; name: string;
    description?: string; dueDate?: string; createdAt?: string;
    assignedTo?: { id: number; staffName: string } | null;
    udin?: string; udinDate?: string; archiveLink?: string;
    fileName?: string; originalName?: string;
    invoice?: { id: number; invoiceNumber: string; status: string; totalAmount?: number } | null;
  }
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [certsLoading, setCertsLoading] = useState(false);
  const [showCertForm, setShowCertForm] = useState(false);
  const [editingCert, setEditingCert] = useState<Certificate | null>(null);
  const [certForm, setCertForm] = useState({ name: '', description: '', assignedToId: '', dueDate: '' });
  // Guards the submit button against a fast double/triple click — without
  // this, each click fires its own POST before the first response comes back
  // to close the form, so N clicks silently create N identical certificates.
  const [certSaving, setCertSaving] = useState(false);

  // Compliances
  interface ComplianceTypeItem { id: number; name: string; frequency: string; dueDay: number; dueMonthOffset: number; isActive?: boolean; }
  interface Enrollment { id: number; complianceTypeId: number; complianceType: ComplianceTypeItem; taxId?: string | null; startPeriod: string; endPeriod?: string; }
  interface ClientTaxOption { value: string; label: string; kind: 'PAN' | 'GSTIN'; }
  interface ComplianceInstance {
    id: number; complianceNumber: string; complianceType: { id: number; name: string; frequency: string };
    periodLabel: string; dueDate: string; escalationDate: string; extendedDueDate?: string; status: string;
    completedOn?: string; remarks?: string; fileName?: string; originalName?: string; taxId?: string | null;
  }
  const [complianceTypes, setComplianceTypes] = useState<ComplianceTypeItem[]>([]);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [complianceInstances, setComplianceInstances] = useState<ComplianceInstance[]>([]);
  const [compliancesLoading, setCompliancesLoading] = useState(false);
  const [showEnrollForm, setShowEnrollForm] = useState(false);
  const [enrollForm, setEnrollForm] = useState({ taxId: '', taxIdSource: 'custom', complianceTypeId: '', startMonth: '', startYear: '', endMonth: '', endYear: '' });
  const [clientTaxOptions, setClientTaxOptions] = useState<ClientTaxOption[]>([]);

  // Assigned staff on task
  const [assignedStaff, setAssignedStaff] = useState<TaskStaffEntry[]>([]);
  const [staffAddId, setStaffAddId] = useState('');
  const [staffAddLoading, setStaffAddLoading] = useState(false);
  const [teamAddPartnerId, setTeamAddPartnerId] = useState('');
  const [teamAddLoading, setTeamAddLoading] = useState(false);

  // Comments
  interface TaskComment { id: number; content: string; createdAt: string; author: { id: number; staffName: string }; }
  const [comments, setComments] = useState<TaskComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [commentSubmitting, setCommentSubmitting] = useState(false);

  // Audit history
  interface AuditEntry { id: number; action: string; entity: string; changes: any; createdAt: string; user: { email: string; staff?: { staffName: string } }; }
  const [auditHistory, setAuditHistory] = useState<AuditEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Bulk operations
  const [selectedTaskIds, setSelectedTaskIds] = useState<number[]>([]);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkAction, setBulkAction] = useState<'CLOSE' | 'REASSIGN_MANAGER' | 'REASSIGN_PARTNER' | 'ASSIGN_STAFF'>('CLOSE');
  const [bulkStaffId, setBulkStaffId] = useState('');
  const [bulkResult, setBulkResult] = useState<{ succeeded: number; failed: number; results: any[] } | null>(null);
  const [bulkLoading, setBulkLoading] = useState(false);

  // One-time master data load (mount only). Task rows load independently
  // below (server-side paged) — mutations should call reloadTasks(), not
  // this, so a single-task edit doesn't re-fetch staff/categories/etc. too.
  const fetchAll = useCallback(async () => {
    const [staffRes, pcRes, catRes, beRes, templatesRes, optRes] = await Promise.all([
      getStaff().catch(() => ({ data: [] })),
      getProfitCentres().catch(() => ({ data: [] })),
      getCategories().catch(() => ({ data: [] })),
      getBillingEntities().catch(() => ({ data: [] })),
      getTaskTemplates().catch(() => ({ data: [] })),
      getTaskFilterOptions().catch(() => ({ data: null })),
    ]);
    setStaff(staffRes.data);
    setProfitCentres(pcRes.data);
    setCategories(catRes.data);
    setBillingEntities(beRes.data);
    setTemplates(templatesRes.data || []);
    if (optRes.data) setFilterOptions(optRes.data);
  }, []);

  useEffect(() => { fetchAll(); }, []);

  // Post-mutation refresh: re-fetch just the current page of tasks, without
  // touching masters. Used by every create/update/delete/bulk handler.
  const reloadTasks = useCallback(() => setRefreshTick((t) => t + 1), []);

  // Debounce the search box so we don't hit the server on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  // Build the server-side query from the active filters. Every filter control
  // already resets page to 1 in its onChange handler.
  const buildTaskParams = useCallback(() => {
    const params: any = { sortField, sortDir };
    if (debouncedSearch) params.search = debouncedSearch;
    if (filterStatus) params.status = filterStatus;
    if (filterBilling) params.billingStatus = filterBilling;
    if (filterClientName) params.clientName = filterClientName;
    if (filterCategoryName) params.categoryName = filterCategoryName;
    if (filterPartnerName) params.partnerName = filterPartnerName;
    if (filterManagerName) params.managerName = filterManagerName;
    if (filterBillingEntity) params.billingEntityName = filterBillingEntity;
    if (filterDueDateFrom) params.dueFrom = filterDueDateFrom;
    if (filterDueDateTo) params.dueTo = filterDueDateTo;
    if (filterCreatedFrom) params.createdFrom = filterCreatedFrom;
    if (filterCreatedTo) params.createdTo = filterCreatedTo;
    if (filterArchive) params.archive = filterArchive;
    if (filterTaskStatus) params.taskStatus = filterTaskStatus;
    if (showOverdueOnly) params.overdue = '1';
    return params;
  }, [debouncedSearch, filterStatus, filterBilling, filterClientName, filterCategoryName,
      filterPartnerName, filterManagerName, filterBillingEntity, filterDueDateFrom, filterDueDateTo,
      filterCreatedFrom, filterCreatedTo, filterArchive, filterTaskStatus, showOverdueOnly, sortField, sortDir]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getTasks({ ...buildTaskParams(), page, pageSize })
      .then((res) => {
        if (cancelled) return;
        const rows = res.data.rows ?? [];
        const rowTotal = res.data.total ?? 0;
        setTasks(rows);
        setTotal(rowTotal);
        // A mutation (e.g. bulk-delete) can shrink the result set below the
        // current page's offset — retreat to the last valid page rather than
        // showing a confusing "No tasks found" with rows still on page 1.
        const lastPage = Math.max(1, Math.ceil(rowTotal / pageSize));
        if (page > lastPage) setPage(lastPage);
      })
      .catch(() => { if (!cancelled) { setTasks([]); setTotal(0); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [buildTaskParams, page, pageSize, refreshTick]);

  // With server-side paging `loading` flips on every page/filter change, so
  // guard with a ref: the URL-requested detail modal must open exactly once,
  // not re-open each time a fetch settles after the user closed it.
  const openedFromUrl = useRef<number | null>(null);
  useEffect(() => {
    if (openTaskId && !loading && openedFromUrl.current !== openTaskId) {
      openedFromUrl.current = openTaskId;
      // openDetail sets detailTab to 'info'; if the caller requested a
      // specific tab via URL (?detailTab=litigations from the firm-wide
      // Litigations list, for example), switch to it after the detail
      // record loads.
      openDetail({ id: openTaskId } as Task).then(() => {
        if (openDetailTab) setDetailTab(openDetailTab);
      });
    }
  }, [openTaskId, loading]); // openDetail is stable

  const handleClientSearch = async (q: string) => {
    setClientSearch(q);
    if (q.length >= 2) {
      const res = await getClients(q);
      setClientResults(res.data);
      setClientDropOpen(true);
    } else {
      setClientResults([]);
      setClientDropOpen(false);
    }
  };

  const selectClientForCreate = (c: Client) => {
    setSelectedClient(c);
    setCreateForm((f) => ({ ...f, clientId: String(c.id) }));
    setClientSearch(`${c.clientCode} — ${c.clientName}`);
    setClientDropOpen(false);
  };

  const openCreate = () => {
    setEditing(null);
    setCreateForm(defaultCreateForm);
    setClientSearch('');
    setClientResults([]);
    setClientDropOpen(false);
    setSelectedClient(null);
    setError('');
    setShowModal(true);
  };

  const openEdit = (task: Task) => {
    setEditing(task);
    // Archive link is now stored encrypted and not returned in list responses.
    // Reveal it just-in-time when the edit modal opens (audited server-side)
    // so the existing value is visible to the editor.
    if ((task.archiveLinkSet || task.archiveLink) && !isCACertification(task)) {
      revealTaskArchiveLink(task.id)
        .then((res) => setEditForm((prev) => ({ ...prev, archiveLink: res.data?.url || '' })))
        .catch(() => { /* leave blank — user can re-enter */ });
    }
    setEditForm({
      taskName: task.taskName,
      partnerId: task.partner?.id ? String(task.partner.id) : '',
      managerId: task.manager?.id ? String(task.manager.id) : '',
      clientId: task.client?.id ? String(task.client.id) : '',
      profitCentreId: task.profitCentre?.id ? String(task.profitCentre.id) : '',
      categoryId: task.category?.id ? String(task.category.id) : '',
      billedAmount: task.billedAmount != null ? String(task.billedAmount) : '',
      billingEntityId: task.billingEntity?.id ? String(task.billingEntity.id) : '',
      status: task.status,
      billingStatus: task.billingStatus,
      billDetails: task.billDetails || '',
      dueDate: task.dueDate ? task.dueDate.slice(0, 10) : '',
      archiveLink: '', // populated asynchronously above via revealTaskArchiveLink
      archivingConfirmed: task.archivingConfirmed ?? false,
      reference: task.reference || '',
      terms: task.terms || '',
    });
    if (task.client) {
      setSelectedClient(task.client);
      setClientSearch(`${task.client.clientCode} — ${task.client.clientName}`);
    } else {
      setSelectedClient(null);
      setClientSearch('');
    }
    setClientResults([]);
    setClientDropOpen(false);
    setError('');
    setShowModal(true);
  };

  const openDetail = async (task: Task) => {
    const res = await api.get(`/tasks/${task.id}`);
    setDetailTask(res.data);
    setAssignedStaff(res.data.assignedStaff || []);
    setDetailTab('info');
    setSubTasks([]);
    setComments([]);
    setAuditHistory([]);
    setStaffAddId('');
    return res.data;
  };

  const loadCertificates = async (taskId: number) => {
    setCertsLoading(true);
    try {
      const res = await getCertificatesApi(taskId);
      setCertificates(res.data);
    } catch { /* ignore */ } finally { setCertsLoading(false); }
  };

  const handleCertTabClick = (task: Task) => {
    setDetailTab('certificates');
    if (certificates.length === 0) loadCertificates(task.id);
  };

  const handleCertSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!detailTask || certSaving) return;
    setCertSaving(true);
    try {
      const payload = {
        name: certForm.name,
        description: certForm.description || null,
        assignedToId: certForm.assignedToId || null,
        dueDate: certForm.dueDate || null,
      };
      if (editingCert) {
        await updateCertificateApi(detailTask.id, editingCert.id, payload);
      } else {
        await createCertificateApi(detailTask.id, payload);
      }
      setShowCertForm(false);
      setEditingCert(null);
      setCertForm({ name: '', description: '', assignedToId: '', dueDate: '' });
      loadCertificates(detailTask.id);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error saving certificate');
    } finally {
      setCertSaving(false);
    }
  };

  const handleDeleteCert = async (certId: number) => {
    if (!detailTask) return;
    if (!window.confirm('Delete this certificate?')) return;
    try {
      await deleteCertificateApi(detailTask.id, certId);
      loadCertificates(detailTask.id);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error deleting certificate');
    }
  };

  // ── Compliance handlers ──
  const loadCompliances = async (taskId: number) => {
    setCompliancesLoading(true);
    try {
      const [enr, inst] = await Promise.all([getTaskEnrollments(taskId), getTaskCompliances(taskId)]);
      setEnrollments(enr.data);
      setComplianceInstances(inst.data);
    } catch { /* silent */ }
    finally { setCompliancesLoading(false); }
  };

  const handleComplianceTabClick = (task: Task) => {
    setDetailTab('compliances');
    loadCompliances(task.id);
    if (complianceTypes.length === 0) {
      getComplianceTypesApi().then(r => setComplianceTypes(r.data)).catch(() => {});
    }
    // Load client PAN + GSTINs so user can pick from existing IDs instead of retyping
    if (task.client?.id) {
      const clientId = task.client.id;
      Promise.all([
        getClientGstins(clientId).catch(() => ({ data: [] })),
        getClientProfile(clientId).catch(() => ({ data: {} })),
      ]).then(([gstRes, profRes]) => {
        const opts: ClientTaxOption[] = [];
        const pan = (profRes.data as any)?.pan;
        if (pan) opts.push({ value: pan, label: `PAN — ${pan}`, kind: 'PAN' });
        (gstRes.data || []).forEach((g: any) => {
          if (g.gstin) opts.push({ value: g.gstin, label: `GSTIN — ${g.gstin}${g.label ? ` (${g.label})` : ''}`, kind: 'GSTIN' });
        });
        setClientTaxOptions(opts);
      });
    } else {
      setClientTaxOptions([]);
    }
  };

  const handleEnrollSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!detailTask || !enrollForm.complianceTypeId || !enrollForm.startMonth || !enrollForm.startYear || enrollSaving) return;
    const taxId = enrollForm.taxId.trim();
    if (!taxId) { alert('Tax ID is required'); return; }
    const startPeriod = `${enrollForm.startYear}-${enrollForm.startMonth}-01`;
    const endPeriod = enrollForm.endMonth && enrollForm.endYear
      ? `${enrollForm.endYear}-${enrollForm.endMonth}-01` : undefined;
    setEnrollSaving(true);
    try {
      await enrollComplianceType(detailTask.id, {
        complianceTypeId: Number(enrollForm.complianceTypeId),
        taxId,
        startPeriod,
        endPeriod,
      });
      setShowEnrollForm(false);
      setEnrollForm({ taxId: '', taxIdSource: 'custom', complianceTypeId: '', startMonth: '', startYear: '', endMonth: '', endYear: '' });
      loadCompliances(detailTask.id);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error adding compliance');
    } finally {
      setEnrollSaving(false);
    }
  };

  const handleRemoveEnrollment = async (enrollId: number) => {
    if (!detailTask) return;
    if (!window.confirm('Remove this compliance type from this task?')) return;
    try {
      await removeEnrollment(detailTask.id, enrollId);
      loadCompliances(detailTask.id);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error removing');
    }
  };

  const handleComplianceStatusChange = async (compId: number, newStatus: string) => {
    try {
      await updateComplianceApi(compId, { status: newStatus });
      if (detailTask) loadCompliances(detailTask.id);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error');
    }
  };

  const handleDeleteCompliance = async (compId: number) => {
    if (!window.confirm('Delete this compliance instance?')) return;
    try {
      await deleteComplianceApi(compId);
      if (detailTask) loadCompliances(detailTask.id);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error');
    }
  };

  const handleAddTaskStaff = async (taskId: number) => {
    if (!staffAddId) return;
    setStaffAddLoading(true);
    try {
      await addTaskStaff(taskId, Number(staffAddId));
      const res = await api.get(`/tasks/${taskId}`);
      setDetailTask(res.data);
      setAssignedStaff(res.data.assignedStaff || []);
      setStaffAddId('');
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error adding staff');
    } finally {
      setStaffAddLoading(false);
    }
  };

  // Bulk-add: every active staff that reports to the chosen partner (plus
  // the partner themselves) goes onto the task in one shot. Skips anyone
  // already on the task server-side, so re-running is safe.
  const handleAddTaskTeam = async (taskId: number) => {
    if (!teamAddPartnerId) return;
    setTeamAddLoading(true);
    try {
      const r = await addTaskTeam(taskId, Number(teamAddPartnerId));
      const res = await api.get(`/tasks/${taskId}`);
      setDetailTask(res.data);
      setAssignedStaff(res.data.assignedStaff || []);
      setTeamAddPartnerId('');
      const added = r.data?.added ?? 0;
      const skipped = r.data?.skipped ?? 0;
      alert(`Added ${added} team member${added === 1 ? '' : 's'}${skipped > 0 ? ` (${skipped} already on the task)` : ''}.`);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error adding partner team');
    } finally {
      setTeamAddLoading(false);
    }
  };

  const handleRemoveTaskStaff = async (taskId: number, staffId: number) => {
    try {
      await removeTaskStaff(taskId, staffId);
      const res = await api.get(`/tasks/${taskId}`);
      setDetailTask(res.data);
      setAssignedStaff(res.data.assignedStaff || []);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error removing staff');
    }
  };

  const loadComments = async (taskId: number) => {
    setCommentsLoading(true);
    try {
      const res = await getTaskComments(taskId);
      setComments(res.data || []);
    } catch { /* ignore */ }
    finally { setCommentsLoading(false); }
  };

  const loadHistory = async (taskId: number) => {
    setHistoryLoading(true);
    try {
      const res = await api.get('/audit', { params: { taskId, limit: 50 } });
      setAuditHistory(res.data?.logs || []);
    } catch { /* ignore */ }
    finally { setHistoryLoading(false); }
  };

  const handleCommentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!detailTask || !commentText.trim()) return;
    setCommentSubmitting(true);
    try {
      await createTaskComment(detailTask.id, { content: commentText.trim() });
      setCommentText('');
      loadComments(detailTask.id);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error posting comment');
    } finally {
      setCommentSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    if (!detailTask) return;
    if (!window.confirm('Delete this comment?')) return;
    try {
      await deleteTaskComment(commentId);
      loadComments(detailTask.id);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error deleting comment');
    }
  };

  const loadSubTasks = async (taskId: number) => {
    setSubTasksLoading(true);
    try {
      const res = await getSubTasks(taskId);
      setSubTasks(res.data);
    } catch { /* silently ignore */ }
    finally { setSubTasksLoading(false); }
  };

  const handleSubTaskTabClick = (task: Task) => {
    setDetailTab('subtasks');
    loadSubTasks(task.id);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (taskSaving) return;
    setError('');
    if (!editing) {
      const missing: string[] = [];
      if (!createForm.clientId) missing.push('Client');
      if (!createForm.partnerId) missing.push('Partner');
      if (!createForm.managerId) missing.push('Manager');
      if (!createForm.categoryId) missing.push('Category');
      if (!createForm.dueDate) missing.push('Due Date');
      if (missing.length > 0) {
        setError(`Required fields missing: ${missing.join(', ')}`);
        return;
      }
    }
    setTaskSaving(true);
    try {
      if (editing) {
        const payload: any = {
          ...editForm,
          partnerId: editForm.partnerId || null,
          managerId: editForm.managerId || null,
          clientId: editForm.clientId || null,
          profitCentreId: editForm.profitCentreId || null,
          categoryId: editForm.categoryId || null,
          billingEntityId: editForm.billingEntityId || null,
          billedAmount: editForm.billedAmount || null,
        };
        // Guard against the reveal-not-finished race: if the editor opened on
        // a task that has an archive link but the form field is still empty
        // (reveal hadn't completed when the user clicked Save), omit
        // archiveLink from the payload so the existing ciphertext is kept.
        const hadArchive = !!(editing.archiveLinkSet || editing.archiveLink);
        if (hadArchive && !editForm.archiveLink) delete payload.archiveLink;
        const saved = await updateTask(editing.id, payload);
        if (saved.data?.claimsNotice) alert(saved.data.claimsNotice);
      } else {
        const payload: any = {
          taskName: createForm.taskName,
          partnerId: createForm.partnerId || null,
          managerId: createForm.managerId || null,
          clientId: createForm.clientId || null,
          categoryId: createForm.categoryId || null,
          dueDate: createForm.dueDate || null,
        };
        await createTask(payload);
      }
      setShowModal(false);
      reloadTasks();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error saving task');
    } finally {
      setTaskSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this task and all associated data?')) return;
    try {
      await deleteTask(id);
      reloadTasks();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error deleting task');
    }
  };

  // ── Inline edit ──────────────────────────────────────────────────────────────
  const startInlineEdit = (taskId: number, field: string, value: string) => {
    setInlineEdit({ taskId, field, value });
  };

  const handleInlineSave = async () => {
    if (!inlineEdit) return;
    try {
      const saved = await updateTask(inlineEdit.taskId, { [inlineEdit.field]: inlineEdit.value || null });
      if (saved.data?.claimsNotice) alert(saved.data.claimsNotice);
      setInlineEdit(null);
      reloadTasks();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error saving');
      setInlineEdit(null);
    }
  };

  const handleInlineKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleInlineSave();
    if (e.key === 'Escape') setInlineEdit(null);
  };

  // ── Template handlers ────────────────────────────────────────────────────────
  const applyTemplate = (templateId: string) => {
    setSelectedTemplateId(templateId);
    if (!templateId) return;
    const tpl = templates.find((t) => String(t.id) === templateId);
    if (!tpl) return;
    setCreateForm((f) => ({
      ...f,
      taskName: tpl.name,
      categoryId: tpl.categoryId ? String(tpl.categoryId) : f.categoryId,
    }));
  };

  const openTemplateManager = () => {
    setEditingTemplate(null);
    setShowTemplateForm(false);
    setTemplateForm({ name: '', description: '', categoryId: '', checklist: '' });
    setShowTemplateModal(true);
  };

  const openEditTemplate = (t: TaskTemplate) => {
    setEditingTemplate(t);
    setTemplateForm({
      name: t.name,
      description: t.description || '',
      categoryId: t.categoryId ? String(t.categoryId) : '',
      checklist: (t.checklist || []).join('\n'),
    });
    setShowTemplateForm(true);
    setShowTemplateModal(true);
  };

  const handleSaveTemplate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (templateSaving) return;
    const payload = {
      name: templateForm.name,
      description: templateForm.description,
      categoryId: templateForm.categoryId ? Number(templateForm.categoryId) : null,
      checklist: templateForm.checklist.split('\n').map((s) => s.trim()).filter(Boolean),
    };
    setTemplateSaving(true);
    try {
      if (editingTemplate) {
        await updateTaskTemplate(editingTemplate.id, payload);
      } else {
        await createTaskTemplate(payload);
      }
      setShowTemplateForm(false);
      setEditingTemplate(null);
      reloadTasks();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error saving template');
    } finally {
      setTemplateSaving(false);
    }
  };

  const handleDeleteTemplate = async (id: number) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await deleteTaskTemplate(id);
      reloadTasks();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error deleting template');
    }
  };

  // ── Bulk operations ──────────────────────────────────────────────────────────
  const toggleSelectTask = (id: number) => {
    setSelectedTaskIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // Toggles the current page's rows in/out of the selection. Selection
  // accumulates across pages (bulk actions send IDs, so rows needn't stay
  // loaded), and clears via the ✕ on the bulk bar.
  const toggleSelectAll = () => {
    const allOnPageSelected = tasks.length > 0 && tasks.every((t) => selectedTaskIds.includes(t.id));
    setSelectedTaskIds((prev) => {
      if (allOnPageSelected) return prev.filter((id) => !tasks.some((t) => t.id === id));
      const next = new Set(prev);
      tasks.forEach((t) => next.add(t.id));
      return Array.from(next);
    });
  };

  const handleBulkSubmit = async () => {
    if (!selectedTaskIds.length) return;
    setBulkLoading(true);
    setBulkResult(null);
    try {
      const payload: any = { taskIds: selectedTaskIds, action: bulkAction };
      if (bulkAction === 'REASSIGN_MANAGER') payload.data = { managerId: Number(bulkStaffId) };
      if (bulkAction === 'REASSIGN_PARTNER') payload.data = { partnerId: Number(bulkStaffId) };
      if (bulkAction === 'ASSIGN_STAFF') payload.data = { staffId: Number(bulkStaffId) };
      const res = await bulkUpdateTasks(payload);
      setBulkResult(res.data);
      if (bulkAction === 'CLOSE') {
        const notices = (res.data.results || []).filter((r: any) => r.success && r.message).map((r: any) => r.message);
        if (notices.length) alert(notices.join('\n\n'));
      }
      reloadTasks();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Bulk update failed');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleQuickConfirmArchive = async (task: Task) => {
    try {
      await confirmTaskArchive(task.id);
      reloadTasks();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error');
    }
  };

  const handleFreezeTask = async (task: Task) => {
    if (!window.confirm(`Freeze task "${task.taskName}"? This will lock the task from further edits.`)) return;
    try {
      const frozen = await freezeTask(task.id);
      if (frozen.data?.claimsNotice) alert(frozen.data.claimsNotice);
      reloadTasks();
      if (detailTask?.id === task.id) {
        const res = await api.get(`/tasks/${task.id}`);
        setDetailTask(res.data);
      }
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error freezing task');
    }
  };

  const handleUnfreezeTask = async (task: Task) => {
    if (!window.confirm(`Unfreeze task "${task.taskName}"?`)) return;
    try {
      await unfreezeTask(task.id);
      reloadTasks();
      if (detailTask?.id === task.id) {
        const res = await api.get(`/tasks/${task.id}`);
        setDetailTask(res.data);
      }
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error unfreezing task');
    }
  };

  // ── Expense & doc handlers ───────────────────────────────────────────────────
  const handleAddExpense = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!detailTask || expenseSaving) return;
    setExpenseSaving(true);
    try {
      await addExpense(detailTask.id, { ...expenseForm, amount: Number(expenseForm.amount) });
      const res = await api.get(`/tasks/${detailTask.id}`);
      setDetailTask(res.data);
      setExpenseForm({ description: '', amount: '', date: format(new Date(), 'yyyy-MM-dd'), category: 'OTHER' });
      reloadTasks();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error adding expense');
    } finally {
      setExpenseSaving(false);
    }
  };

  const handleDeleteExpense = async (expId: number) => {
    if (!detailTask) return;
    await deleteExpense(expId);
    const res = await api.get(`/tasks/${detailTask.id}`);
    setDetailTask(res.data);
    reloadTasks();
  };

  const handleUploadDoc = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!detailTask || !e.target.files?.[0]) return;
    setUploadingDoc(true);
    const formData = new FormData();
    formData.append('file', e.target.files[0]);
    await api.post(`/documents/task/${detailTask.id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    const res = await api.get(`/tasks/${detailTask.id}`);
    setDetailTask(res.data);
    setUploadingDoc(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleDeleteDoc = async (docId: number) => {
    if (!detailTask) return;
    await api.delete(`/documents/${docId}`);
    const res = await api.get(`/tasks/${detailTask.id}`);
    setDetailTask(res.data);
  };

  const handleSubTaskSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!detailTask || subTaskSaving) return;
    setSubTaskSaving(true);
    try {
      if (editingSubTask) {
        await updateSubTaskApi(editingSubTask.id, {
          name: subTaskForm.name,
          description: subTaskForm.description || null,
          assignedToId: subTaskForm.assignedToId || null,
          dueDate: subTaskForm.dueDate || null,
        });
      } else {
        await createSubTaskApi(detailTask.id, {
          name: subTaskForm.name,
          description: subTaskForm.description || null,
          assignedToId: subTaskForm.assignedToId || null,
          dueDate: subTaskForm.dueDate || null,
        });
      }
      setShowSubTaskForm(false);
      setEditingSubTask(null);
      setSubTaskForm({ name: '', description: '', assignedToId: '', dueDate: '' });
      loadSubTasks(detailTask.id);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error saving sub-task');
    } finally {
      setSubTaskSaving(false);
    }
  };

  const handleSubTaskStatusChange = async (st: SubTask, newStatus: SubTask['status']) => {
    if (!detailTask) return;
    try {
      if (newStatus === 'CLOSED') {
        await closeSubTaskApi(st.id);
      } else {
        await updateSubTaskApi(st.id, { status: newStatus });
      }
      loadSubTasks(detailTask.id);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error updating sub-task');
    }
  };

  const handleDeleteSubTask = async (stId: number) => {
    if (!detailTask) return;
    if (!window.confirm('Delete this sub-task?')) return;
    try {
      await deleteSubTaskApi(stId);
      loadSubTasks(detailTask.id);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error deleting sub-task');
    }
  };

  const openEditSubTask = (st: SubTask) => {
    setEditingSubTask(st);
    setSubTaskForm({
      name: st.name,
      description: st.description || '',
      assignedToId: st.assignedTo?.id ? String(st.assignedTo.id) : '',
      dueDate: st.dueDate ? st.dueDate.slice(0, 10) : '',
    });
    setShowSubTaskForm(true);
  };

  const exportExcel = async () => {
    // Fetch the full filtered set from the server (no page param → full
    // array), so the export still covers every matching row, not just the
    // page in view.
    let rows: Task[] = [];
    try {
      const res = await getTasks(buildTaskParams());
      rows = res.data || [];
    } catch { alert('Export failed.'); return; }
    const data = rows.map((t) => ({
      'Task ID': t.taskId, 'Task Name': t.taskName,
      'Client': t.client?.clientName || '',
      'Partner': t.partner?.staffName || '', 'Manager': t.manager?.staffName || '',
      'Category': t.category?.name || '', 'Status': t.status,
      'Billing Status': t.billingStatus, 'Due Date': t.dueDate ? t.dueDate.slice(0, 10) : '',
      'Cost Incurred (₹)': t.costIncurred ?? '', 'OPE Incurred (₹)': t.opeIncurred ?? '',
      'Billed Amount (₹)': t.billedAmount ?? '',
      // Archive URL deliberately omitted from exports — it points at the
      // internal file server and must not leave the app. "Archived" is just
      // the presence flag.
      'Archived': (t.archiveLinkSet || t.archiveLink) ? 'Yes' : 'No',
      'Archiving Confirmed': t.archivingConfirmed ? 'Yes' : 'No',
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Tasks');
    XLSX.writeFile(wb, 'Tasks.xlsx');
  };

  // Filtering, sorting and paging all happen server-side now — `tasks` is
  // already the current page of the filtered result. Dropdown options come
  // from /tasks/filter-options (distinct values across the whole dataset).
  const toOptions = (names: string[]): FilterOption[] => names.map((n) => ({ value: n, label: n }));
  const clientColOptions = React.useMemo(() => toOptions(filterOptions.clients), [filterOptions.clients]);
  const categoryColOptions = React.useMemo(() => toOptions(filterOptions.categories), [filterOptions.categories]);
  const partnerColOptions = React.useMemo(() => toOptions(filterOptions.partners), [filterOptions.partners]);
  const managerColOptions = React.useMemo(() => toOptions(filterOptions.managers), [filterOptions.managers]);
  const billingEntityColOptions = React.useMemo(() => toOptions(filterOptions.billingEntities), [filterOptions.billingEntities]);

  const handleTaskSort = (f: typeof sortField) => {
    if (sortField === f) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(f); setSortDir('asc'); }
    setPage(1);
  };
  const TaskSortIcon = ({ f }: { f: typeof sortField }) =>
    sortField !== f ? <span className="ml-1 text-gray-300">↕</span>
    : <span className="ml-1 text-blue-600">{sortDir === 'asc' ? '↑' : '↓'}</span>;

  const totalTaskPages = Math.max(1, Math.ceil(total / pageSize));
  const pagedTasks = tasks;

  const fileSizeStr = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  const activeStaff = staff.filter((s: any) => s.isActive !== false);
  const partners = staff.filter((s: any) => s.isPartner && s.isActive !== false);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Task List</h2>
        <div className="flex gap-2">
          <button className="btn-secondary text-sm" onClick={exportExcel}>Export Excel</button>
          {(isAdmin || isPartner) && (
            <button className="btn-secondary text-sm" onClick={openTemplateManager}>📋 Templates</button>
          )}
          <button className="btn-primary" onClick={openCreate}>+ Add Task</button>
        </div>
      </div>

      {/* Bulk action bar */}
      {selectedTaskIds.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-center justify-between">
          <span className="text-sm font-medium text-blue-800">{selectedTaskIds.length} task{selectedTaskIds.length !== 1 ? 's' : ''} selected</span>
          <div className="flex gap-2">
            <button className="btn-secondary text-sm" onClick={() => setSelectedTaskIds([])}>Clear</button>
            <button className="btn-primary text-sm" onClick={() => { setBulkResult(null); setBulkAction('CLOSE'); setBulkStaffId(''); setShowBulkModal(true); }}>
              Bulk Actions
            </button>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-gray-500 flex-wrap">
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-green-500 inline-block" /> Open</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-gray-400 inline-block" /> Closed</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-blue-500 inline-block" /> Billed</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-white border-2 border-gray-400 inline-block" /> Unbilled</span>
        <span className="text-gray-400">· Click Task ID or Name to view details · Click Archive to edit inline</span>
      </div>

      <div className="card">
        <div className="flex flex-wrap gap-2 items-center mb-3">
          <input className="input-field w-56 text-sm" placeholder="Search tasks..." value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
          <select className="input-field w-32 text-sm" value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}>
            <option value="">All Statuses</option>
            <option value="OPEN">Open</option>
            <option value="CLOSED">Closed</option>
          </select>
          <label className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-sm font-medium cursor-pointer transition-colors ${showOverdueOnly ? 'bg-red-100 text-red-700 border-red-300' : 'bg-white text-gray-600 border-gray-300 hover:border-red-400'}`}>
            <input type="checkbox" className="sr-only" checked={showOverdueOnly} onChange={(e) => { setShowOverdueOnly(e.target.checked); setPage(1); }} />
            {showOverdueOnly ? '⚠ Overdue only' : 'Overdue only'}
          </label>
          <select className="input-field w-32 text-sm" value={filterBilling} onChange={(e) => { setFilterBilling(e.target.value); setPage(1); }}>
            <option value="">All Billing</option>
            <option value="BILLED">Billed</option>
            <option value="UNBILLED">Unbilled</option>
          </select>
          <div className="flex items-center gap-1 text-sm text-gray-500">
            <span className="text-xs">Due:</span>
            <input type="date" className="input-field text-xs py-1 px-2" value={filterDueDateFrom}
              onChange={e => { setFilterDueDateFrom(e.target.value); setPage(1); }} title="Due date from" />
            <span className="text-xs text-gray-400">–</span>
            <input type="date" className="input-field text-xs py-1 px-2" value={filterDueDateTo}
              onChange={e => { setFilterDueDateTo(e.target.value); setPage(1); }} title="Due date to" />
          </div>
          <span className="text-sm text-gray-400 ml-auto">{total} tasks</span>
        </div>

        {loading ? <p className="text-gray-500 text-sm">Loading...</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="table-header w-8">
                    <input type="checkbox" className="w-4 h-4 rounded"
                      checked={tasks.length > 0 && tasks.every((t) => selectedTaskIds.includes(t.id))}
                      onChange={toggleSelectAll} />
                  </th>
                  <th className="table-header cursor-pointer select-none" onClick={() => handleTaskSort('taskId')}>
                    <span className="font-semibold">Task ID</span> <TaskSortIcon f="taskId" />
                  </th>
                  <th className="table-header cursor-pointer select-none" onClick={() => handleTaskSort('taskName')}>
                    <span className="font-semibold">Task Name</span> <TaskSortIcon f="taskName" />
                  </th>
                  <th className="table-header">
                    <ColumnFilter label="Client" options={clientColOptions}
                      value={filterClientName} onChange={(v) => { setFilterClientName(v); setPage(1); }}
                      sortable onSort={() => handleTaskSort('client')} sortIcon={<TaskSortIcon f="client" />} />
                  </th>
                  <th className="table-header">
                    <ColumnFilter label="Category" options={categoryColOptions}
                      value={filterCategoryName} onChange={(v) => { setFilterCategoryName(v); setPage(1); }}
                      sortable onSort={() => handleTaskSort('category')} sortIcon={<TaskSortIcon f="category" />} />
                  </th>
                  <th className="table-header">
                    <ColumnFilter label="Partner" options={partnerColOptions}
                      value={filterPartnerName} onChange={(v) => { setFilterPartnerName(v); setPage(1); }}
                      sortable onSort={() => handleTaskSort('partner')} sortIcon={<TaskSortIcon f="partner" />} />
                  </th>
                  <th className="table-header">
                    <ColumnFilter label="Manager" options={managerColOptions}
                      value={filterManagerName} onChange={(v) => { setFilterManagerName(v); setPage(1); }}
                      sortable onSort={() => handleTaskSort('manager')} sortIcon={<TaskSortIcon f="manager" />} />
                  </th>
                  <th className="table-header w-24">
                    <DateRangeFilter label="Created"
                      fromValue={filterCreatedFrom} toValue={filterCreatedTo}
                      onChange={(f, t) => { setFilterCreatedFrom(f); setFilterCreatedTo(t); setPage(1); }}
                      sortable onSort={() => handleTaskSort('createdAt')} sortIcon={<TaskSortIcon f="createdAt" />} />
                  </th>
                  <th className="table-header w-24">
                    <DateRangeFilter label="Due Date"
                      fromValue={filterDueDateFrom} toValue={filterDueDateTo}
                      onChange={(f, t) => { setFilterDueDateFrom(f); setFilterDueDateTo(t); setPage(1); }}
                      sortable onSort={() => handleTaskSort('dueDate')} sortIcon={<TaskSortIcon f="dueDate" />} />
                  </th>
                  {canSeeCostColumn && <th className="table-header text-right cursor-pointer select-none" onClick={() => handleTaskSort('costIncurred')}><span className="font-semibold">Cost</span> <TaskSortIcon f="costIncurred" /></th>}
                  {canSeeCostColumn && <th className="table-header text-right cursor-pointer select-none" onClick={() => handleTaskSort('opeIncurred')}><span className="font-semibold">OPE</span> <TaskSortIcon f="opeIncurred" /></th>}
                  {canSeeBillingColumn && <th className="table-header text-right cursor-pointer select-none" onClick={() => handleTaskSort('billedAmount')}><span className="font-semibold">Billed</span> <TaskSortIcon f="billedAmount" /></th>}
                  {canSeeBillingColumn && <th className="table-header">
                    <ColumnFilter label="Billing Entity" options={billingEntityColOptions}
                      value={filterBillingEntity} onChange={(v) => { setFilterBillingEntity(v); setPage(1); }}
                      sortable onSort={() => handleTaskSort('billingEntity')} sortIcon={<TaskSortIcon f="billingEntity" />} />
                  </th>}
                  <th className="table-header">
                    <ColumnFilter label="Archive"
                      options={[
                        { value: 'CONFIRMED', label: 'Confirmed' },
                        { value: 'PENDING', label: 'Link pending' },
                        { value: 'NONE', label: 'No archive' },
                      ]}
                      value={filterArchive}
                      onChange={(v) => { setFilterArchive(v); setPage(1); }}
                      sortable onSort={() => handleTaskSort('archive')} sortIcon={<TaskSortIcon f="archive" />} />
                  </th>
                  <th className="table-header">
                    <ColumnFilter label="St."
                      options={[
                        { value: 'OPEN', label: 'Open' },
                        { value: 'CLOSED', label: 'Closed' },
                        { value: 'FROZEN', label: 'Frozen' },
                      ]}
                      value={filterTaskStatus}
                      onChange={(v) => { setFilterTaskStatus(v); setPage(1); }}
                      sortable onSort={() => handleTaskSort('status')} sortIcon={<TaskSortIcon f="status" />} />
                  </th>
                  <th className="table-header"><span className="font-semibold">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {pagedTasks.map((task) => (
                  <tr key={task.id} className={`hover:bg-gray-50 ${task.isOverdue ? 'bg-red-50 hover:bg-red-100' : ''} ${selectedTaskIds.includes(task.id) ? 'bg-blue-50' : ''}`}>
                    <td className="table-cell w-8">
                      <input type="checkbox" className="w-4 h-4 rounded"
                        checked={selectedTaskIds.includes(task.id)}
                        onChange={() => toggleSelectTask(task.id)} />
                    </td>

                    {/* Task ID — click to open detail */}
                    <td className="table-cell">
                      <button
                        className="font-mono font-medium text-blue-700 hover:text-blue-900 hover:underline text-left whitespace-nowrap"
                        onClick={() => openDetail(task)}
                        title="Click to view details"
                      >
                        {task.taskId}
                      </button>
                    </td>

                    {/* Task Name — click to open detail */}
                    <td className="table-cell font-medium max-w-[160px]">
                      <button
                        className="text-left hover:text-blue-700 w-full"
                        onClick={() => openDetail(task)}
                        title="Click to view details"
                      >
                        <div className="flex items-center gap-1">
                          <span className="truncate">{task.taskName}</span>
                          {task.isOverdue && <span className="text-red-500 text-xs font-bold shrink-0">!</span>}
                        </div>
                      </button>
                    </td>

                    <td className="table-cell text-gray-500 max-w-[120px] truncate" title={task.client?.clientName || ''}>{task.client?.clientName || '—'}</td>

                    <td className="table-cell text-gray-500 text-xs" title={task.category?.name || ''}>{task.category?.name || '—'}</td>
                    <td className="table-cell" title={task.partner?.staffName || ''}>{task.partner?.staffName || '—'}</td>
                    <td className="table-cell" title={task.manager?.staffName || ''}>{task.manager?.staffName || '—'}</td>
                    <td className="table-cell text-xs text-gray-500 w-24">
                      {task.createdAt ? format(new Date(task.createdAt), 'dd-MMM-yy') : '—'}
                    </td>
                    <td className="table-cell text-xs w-24">
                      {task.dueDate ? (
                        <span className={task.isOverdue ? 'text-red-600 font-medium' : 'text-gray-600'}>
                          {format(new Date(task.dueDate), 'dd-MMM-yy')}
                        </span>
                      ) : '—'}
                    </td>

                    {canSeeCostColumn && (
                      <td className="table-cell text-right">
                        {canSeeCostForTask(task) && task.costIncurred != null ? fmt(task.costIncurred) : '—'}
                      </td>
                    )}
                    {canSeeCostColumn && (
                      <td className="table-cell text-right">
                        {canSeeCostForTask(task) && task.opeIncurred != null ? fmt(task.opeIncurred) : '—'}
                      </td>
                    )}
                    {canSeeBillingColumn && (
                      <td className="table-cell text-right">
                        {canSeeBillingForTask(task) && task.billedAmount != null ? fmt(Number(task.billedAmount)) : '—'}
                      </td>
                    )}
                    {canSeeBillingColumn && (
                      <td className="table-cell text-gray-500 text-xs">
                        {canSeeBillingForTask(task) ? (task.billingEntity?.name || '—') : '—'}
                      </td>
                    )}

                    {/* Archive — protected: URL is never embedded in the page.
                        "Open" fetches the decrypted link from the server (audited);
                        "Edit" reveals it just for the inline-edit input. */}
                    <td className="table-cell text-center min-w-[100px]">
                      {isCACertification(task) ? (
                        <span className="text-xs text-gray-400">See Certificates</span>
                      ) : inlineEdit?.taskId === task.id && inlineEdit.field === 'archiveLink' ? (
                        <input
                          autoFocus
                          className="border border-blue-400 rounded px-1 py-0.5 text-xs w-36 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          value={inlineEdit.value}
                          placeholder="https://..."
                          onChange={(e) => setInlineEdit({ ...inlineEdit, value: e.target.value })}
                          onBlur={handleInlineSave}
                          onKeyDown={handleInlineKeyDown}
                        />
                      ) : (() => {
                        const hasArchive = !!task.archiveLinkSet || !!task.archiveLink;
                        const openArchive = async () => {
                          // Open a blank window SYNCHRONOUSLY (inside the click handler)
                          // so the browser treats it as a user gesture and the popup
                          // blocker doesn't kill it. We'll set its URL after the reveal.
                          const w = window.open('', '_blank', 'noopener,noreferrer');
                          try {
                            const res = await revealTaskArchiveLink(task.id);
                            const url = res.data?.url || '';
                            if (!url) {
                              if (w) w.close();
                              showToast('Archive link is empty', 'error');
                              return;
                            }
                            // Browsers (Chrome 90+, Edge, Firefox) refuse to navigate
                            // to UNC / file:// paths from web context. Fall back to
                            // copy-to-clipboard so the user can paste into Explorer.
                            const isWebUrl = /^https?:\/\//i.test(url);
                            if (!isWebUrl) {
                              if (w) w.close();
                              try { await navigator.clipboard.writeText(url); } catch { /* ignore */ }
                              showToast(
                                `✓ Archive path copied — paste into Windows Explorer\n${url}`,
                                'success',
                                6000,
                              );
                              return;
                            }
                            if (w) w.location.href = url;
                            else window.open(url, '_blank', 'noopener,noreferrer');
                          } catch (err: any) {
                            if (w) w.close();
                            showToast(err?.response?.data?.message || 'Failed to open archive link', 'error');
                          }
                        };
                        const editArchive = async () => {
                          if (!canSetArchiveLinkForTask(task)) return;
                          let current = '';
                          if (hasArchive) {
                            try {
                              const res = await revealTaskArchiveLink(task.id);
                              current = res.data?.url || '';
                            } catch { /* fall through with empty */ }
                          }
                          startInlineEdit(task.id, 'archiveLink', current);
                        };
                        return (
                          <div className="flex flex-col items-center gap-0.5">
                            {hasArchive ? (
                              <div className="flex items-center gap-1">
                                <button
                                  type="button"
                                  onClick={openArchive}
                                  className={`text-xs rounded px-1.5 py-0.5 font-medium ${task.archivingConfirmed ? 'text-green-700 bg-green-50 hover:bg-green-100' : 'text-indigo-700 bg-indigo-50 hover:bg-indigo-100'}`}
                                  title="Open archive (logged)"
                                >
                                  {task.archivingConfirmed ? '✓ Open' : '🔗 Open'}
                                </button>
                                {canSetArchiveLinkForTask(task) && (
                                  <button
                                    type="button"
                                    onClick={editArchive}
                                    className="text-xs text-gray-400 hover:text-gray-700"
                                    title="Edit archive link"
                                  >✎</button>
                                )}
                              </div>
                            ) : (
                              <span
                                className={`text-xs rounded px-1 ${canSetArchiveLinkForTask(task) ? 'cursor-pointer hover:bg-gray-100 text-gray-400' : 'text-gray-300'}`}
                                title={canSetArchiveLinkForTask(task) ? 'Click to add archive link' : '—'}
                                onClick={() => canSetArchiveLinkForTask(task) && startInlineEdit(task.id, 'archiveLink', '')}
                              >
                                {canSetArchiveLinkForTask(task) ? '+ Link' : '—'}
                              </span>
                            )}
                            {hasArchive && !task.archivingConfirmed && canConfirmArchiving(task) && (
                              <button
                                className="text-xs text-green-600 hover:text-green-800 font-medium bg-green-50 hover:bg-green-100 px-1.5 py-0.5 rounded transition-colors"
                                onClick={() => handleQuickConfirmArchive(task)}
                                title="Mark archiving as confirmed"
                              >
                                Confirm ✓
                              </button>
                            )}
                          </div>
                        );
                      })()}
                    </td>

                    <td className="table-cell">
                      <StatusDot status={task.status} billing={task.billingStatus} />
                    </td>

                    <td className="table-cell">
                      <div className="flex gap-2 items-center flex-wrap">
                        <button className="text-blue-600 hover:text-blue-800 font-medium text-xs" onClick={() => openEdit(task)}>Edit</button>
                        {canDeleteTask(task) && (
                          <button className="text-red-600 hover:text-red-800 font-medium text-xs" onClick={() => handleDelete(task.id)}>Del</button>
                        )}
                        {canSeeBillingForTask(task) && task.client && (task.billingStatus === 'UNBILLED') && (
                          <button
                            className="text-emerald-600 hover:text-emerald-800 font-medium text-xs whitespace-nowrap"
                            onClick={() => navigate(`/invoices?taskId=${task.id}`)}
                            title="Create invoice for this task"
                          >
                            + Invoice
                          </button>
                        )}
                        {task.status === 'CLOSED' && (task.archivingConfirmed || isCACertification(task)) && !task.isFrozen && (isAdmin || isPartner) && (
                          <button
                            className="text-blue-500 hover:text-blue-700 font-medium text-xs whitespace-nowrap"
                            onClick={() => handleFreezeTask(task)}
                            title="Freeze this task"
                          >
                            Freeze
                          </button>
                        )}
                        {task.isFrozen && isAdmin && (
                          <button
                            className="text-orange-500 hover:text-orange-700 font-medium text-xs whitespace-nowrap"
                            onClick={() => handleUnfreezeTask(task)}
                            title="Unfreeze this task"
                          >
                            Unfreeze
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {tasks.length === 0 && (
                  <tr><td colSpan={20} className="table-cell text-center text-gray-400 py-8">No tasks found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        {/* Pagination */}
        {totalTaskPages > 1 && (
          <div className="flex items-center justify-between mt-3">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>Rows per page:</span>
              <select className="border rounded px-2 py-1 text-sm" value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
                {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-1">
              <button className="px-2 py-1 text-sm border rounded disabled:opacity-40" disabled={page === 1} onClick={() => setPage(1)}>«</button>
              <button className="px-2 py-1 text-sm border rounded disabled:opacity-40" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
              <span className="px-3 py-1 text-sm">{page} / {totalTaskPages}</span>
              <button className="px-2 py-1 text-sm border rounded disabled:opacity-40" disabled={page === totalTaskPages} onClick={() => setPage(p => p + 1)}>›</button>
              <button className="px-2 py-1 text-sm border rounded disabled:opacity-40" disabled={page === totalTaskPages} onClick={() => setPage(totalTaskPages)}>»</button>
            </div>
          </div>
        )}
      </div>

      {/* ── Create / Edit Modal ─────────────────────────────────────────────────── */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{editing ? 'Edit Task' : 'Create Task'}</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              {!editing ? (
                /* ── CREATE FORM ── */
                <div className="grid grid-cols-2 gap-4">
                  {templates.length > 0 && (
                    <div className="col-span-2">
                      <label className="label">Use Template (optional)</label>
                      <select className="input-field" value={selectedTemplateId}
                        onChange={(e) => applyTemplate(e.target.value)}>
                        <option value="">— Start from scratch —</option>
                        {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                      </select>
                      {selectedTemplateId && (() => {
                        const tpl = templates.find((t) => String(t.id) === selectedTemplateId);
                        return tpl?.checklist?.length ? (
                          <div className="mt-2 p-2 bg-blue-50 rounded-lg text-xs text-blue-700">
                            <p className="font-semibold mb-1">Checklist:</p>
                            <ul className="list-disc pl-4 space-y-0.5">
                              {tpl.checklist.map((item, i) => <li key={i}>{item}</li>)}
                            </ul>
                          </div>
                        ) : null;
                      })()}
                    </div>
                  )}
                  <div className="col-span-2">
                    <label className="label">Task Name <span className="text-red-500">*</span></label>
                    <input className="input-field" value={createForm.taskName}
                      onChange={(e) => setCreateForm({ ...createForm, taskName: e.target.value })} required />
                  </div>

                  <div className="col-span-2 relative">
                    <label className="label">Client <span className="text-red-500">*</span></label>
                    <input
                      className="input-field"
                      value={clientSearch}
                      placeholder="Type 2+ chars to search clients..."
                      onChange={(e) => handleClientSearch(e.target.value)}
                      onFocus={() => { if (clientResults.length > 0) setClientDropOpen(true); }}
                      autoComplete="off"
                    />
                    {clientDropOpen && clientResults.length > 0 && (
                      <div className="absolute z-10 w-full bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto">
                        {clientResults.map((c) => (
                          <button key={c.id} type="button"
                            className="w-full text-left px-4 py-2 hover:bg-blue-50 text-sm"
                            onClick={() => selectClientForCreate(c)}>
                            <span className="font-mono text-blue-700 text-xs mr-2">{c.clientCode}</span>{c.clientName}
                          </button>
                        ))}
                      </div>
                    )}
                    {selectedClient && <p className="text-xs text-green-600 mt-1">Selected: {selectedClient.clientCode} — {selectedClient.clientName}</p>}
                  </div>

                  <div>
                    <label className="label">Partner <span className="text-red-500">*</span></label>
                    <select className="input-field" value={createForm.partnerId}
                      onChange={(e) => setCreateForm({ ...createForm, partnerId: e.target.value })}>
                      <option value="">— Select Partner —</option>
                      {partners.map((s) => <option key={s.id} value={s.id}>{s.staffName}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="label">Manager <span className="text-red-500">*</span></label>
                    <select className="input-field" value={createForm.managerId}
                      onChange={(e) => setCreateForm({ ...createForm, managerId: e.target.value })}>
                      <option value="">— Select Manager —</option>
                      {activeStaff.map((s) => <option key={s.id} value={s.id}>{s.staffName}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="label">Category <span className="text-red-500">*</span></label>
                    <select className="input-field" value={createForm.categoryId}
                      onChange={(e) => setCreateForm({ ...createForm, categoryId: e.target.value })}>
                      <option value="">— Select —</option>
                      {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="label">Due Date <span className="text-red-500">*</span></label>
                    <input type="date" className="input-field" value={createForm.dueDate}
                      onChange={(e) => setCreateForm({ ...createForm, dueDate: e.target.value })} />
                  </div>
                </div>
              ) : (
                /* ── EDIT FORM ── */
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="label">Task Name <span className="text-red-500">*</span></label>
                    <input className="input-field" value={editForm.taskName}
                      onChange={(e) => setEditForm({ ...editForm, taskName: e.target.value })} required />
                  </div>

                  <div className="col-span-2 relative">
                    <label className="label">Client</label>
                    <input
                      className="input-field"
                      value={clientSearch}
                      placeholder="Type 2+ chars to search clients..."
                      onChange={(e) => {
                        handleClientSearch(e.target.value);
                        setEditForm((f) => ({ ...f, clientId: '' }));
                        setSelectedClient(null);
                      }}
                      onFocus={() => { if (clientResults.length > 0) setClientDropOpen(true); }}
                      autoComplete="off"
                    />
                    {clientDropOpen && clientResults.length > 0 && (
                      <div className="absolute z-10 w-full bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto">
                        {clientResults.map((c) => (
                          <button key={c.id} type="button"
                            className="w-full text-left px-4 py-2 hover:bg-blue-50 text-sm"
                            onClick={() => {
                              setSelectedClient(c);
                              setEditForm((f) => ({ ...f, clientId: String(c.id) }));
                              setClientSearch(`${c.clientCode} — ${c.clientName}`);
                              setClientDropOpen(false);
                            }}>
                            <span className="font-mono text-blue-700 text-xs mr-2">{c.clientCode}</span>{c.clientName}
                          </button>
                        ))}
                      </div>
                    )}
                    {selectedClient && <p className="text-xs text-green-600 mt-1">Selected: {selectedClient.clientCode} — {selectedClient.clientName}</p>}
                  </div>

                  <div>
                    <label className="label">Due Date</label>
                    <input type="date" className="input-field" value={editForm.dueDate}
                      onChange={(e) => setEditForm({ ...editForm, dueDate: e.target.value })} />
                  </div>

                  <div>
                    <label className="label">Status</label>
                    <select className="input-field" value={editForm.status}
                      onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}>
                      <option value="OPEN">Open</option>
                      <option value="CLOSED">Closed</option>
                    </select>
                  </div>

                  <div>
                    <label className="label">Partner</label>
                    <select className="input-field" value={editForm.partnerId}
                      onChange={(e) => setEditForm({ ...editForm, partnerId: e.target.value })}>
                      <option value="">— Select Partner —</option>
                      {partners.map((s) => <option key={s.id} value={s.id}>{s.staffName}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className="label">Manager</label>
                    <select className="input-field" value={editForm.managerId}
                      onChange={(e) => setEditForm({ ...editForm, managerId: e.target.value })}>
                      <option value="">— Select Manager —</option>
                      {activeStaff.map((s) => <option key={s.id} value={s.id}>{s.staffName}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className="label">Category</label>
                    <select className="input-field" value={editForm.categoryId}
                      onChange={(e) => setEditForm({ ...editForm, categoryId: e.target.value })}>
                      <option value="">— Select —</option>
                      {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>

                  {(isAdmin || isPartner) && (
                    <div>
                      <label className="label">Profit Centre</label>
                      <select className="input-field" value={editForm.profitCentreId}
                        onChange={(e) => setEditForm({ ...editForm, profitCentreId: e.target.value })}>
                        <option value="">— Select —</option>
                        {profitCentres.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                    </div>
                  )}

                  {/* Billing fields */}
                  {canSeeBillingForTask(editing!) && (
                    <>
                      <div>
                        <label className="label">Billing Status</label>
                        <select className="input-field" value={editForm.billingStatus}
                          onChange={(e) => setEditForm({ ...editForm, billingStatus: e.target.value })}>
                          <option value="UNBILLED">Unbilled</option>
                          <option value="BILLED">Billed</option>
                        </select>
                      </div>
                      <div>
                        <label className="label">Billing Entity</label>
                        <select className="input-field" value={editForm.billingEntityId}
                          onChange={(e) => setEditForm({ ...editForm, billingEntityId: e.target.value })}>
                          <option value="">— Select —</option>
                          {billingEntities.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="label">Billed Amount (₹)</label>
                        <input type="number" min="0" step="0.01" className="input-field" placeholder="0.00"
                          value={editForm.billedAmount}
                          onChange={(e) => setEditForm({ ...editForm, billedAmount: e.target.value })} />
                      </div>
                      <div className="col-span-2">
                        <label className="label">Bill Details</label>
                        <textarea className="input-field" rows={2} value={editForm.billDetails}
                          onChange={(e) => setEditForm({ ...editForm, billDetails: e.target.value })} />
                      </div>
                    </>
                  )}

                  {!isCACertification(editing!) && (
                    <div className="col-span-2">
                      <label className="label">Archive Link</label>
                      <input className="input-field" value={editForm.archiveLink} placeholder="https://..."
                        onChange={(e) => setEditForm({ ...editForm, archiveLink: e.target.value })} />
                    </div>
                  )}

                  {!isCACertification(editing!) && (isAdmin || user?.staffId === editing?.manager?.id) && (
                    <div className="col-span-2 flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="archivingConfirmed"
                        className="w-4 h-4 text-blue-600 rounded"
                        checked={Boolean(editForm.archivingConfirmed)}
                        onChange={(e) => setEditForm({ ...editForm, archivingConfirmed: e.target.checked })}
                      />
                      <label htmlFor="archivingConfirmed" className="text-sm font-medium text-gray-700">
                        Archiving Confirmed
                      </label>
                    </div>
                  )}

                  {/* Reference & Terms — only for task partner / reporting partner / admin */}
                  {canSeeRefTermsForTask(editing!) && (
                    <>
                      <div className="col-span-2 border-t border-gray-100 pt-3">
                        <p className="text-xs font-semibold text-amber-600 uppercase tracking-wide mb-3">Confidential — Partner Only</p>
                      </div>
                      <div className="col-span-2">
                        <label className="label">Reference</label>
                        <input className="input-field" value={editForm.reference} placeholder="Reference number or note"
                          onChange={(e) => setEditForm({ ...editForm, reference: e.target.value })} />
                      </div>
                      <div className="col-span-2">
                        <label className="label">Terms</label>
                        <textarea className="input-field" rows={3} value={editForm.terms} placeholder="Engagement terms / conditions"
                          onChange={(e) => setEditForm({ ...editForm, terms: e.target.value })} />
                      </div>
                    </>
                  )}
                </div>
              )}

              {error && <p className="text-red-600 text-sm">{error}</p>}
              <div className="flex gap-3 justify-end pt-2">
                <button type="button" className="btn-secondary" disabled={taskSaving} onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={taskSaving}>
                  {taskSaving ? 'Saving…' : editing ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Task Detail Modal ────────────────────────────────────────────────────── */}
      {detailTask && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold">{detailTask.taskId} — {detailTask.taskName}</h3>
                <p className="text-sm text-gray-500">{detailTask.client?.clientName}</p>
              </div>
              <button onClick={() => setDetailTask(null)} className="text-gray-400 hover:text-gray-600 text-xl">&#x2715;</button>
            </div>
            {/* Tab Bar */}
            <div className="flex border-b border-gray-200 px-5 overflow-x-auto">
              {([
                { key: 'info',         label: 'Info' },
                { key: 'expenses',     label: 'OPE / Expenses' },
                { key: 'documents',    label: 'Documents' },
                { key: 'subtasks',     label: 'Sub-Tasks' },
                { key: 'certificates', label: '📋 Certificates' },
                { key: 'litigations',  label: '⚖ Litigations' },
                { key: 'compliances',  label: '📅 Compliances' },
                { key: 'comments',     label: `💬 Comments${comments.length > 0 ? ` (${comments.length})` : ''}` },
                { key: 'history',      label: '📜 History' },
              ] as const).map(({ key, label }) => (
                <button
                  key={key}
                  className={`px-4 py-2 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${detailTab === key ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                  onClick={() => {
                    if (key === 'subtasks')     { handleSubTaskTabClick(detailTask); }
                    else if (key === 'certificates') { handleCertTabClick(detailTask); }
                    else if (key === 'compliances') { handleComplianceTabClick(detailTask); }
                    else if (key === 'comments') { setDetailTab('comments'); loadComments(detailTask.id); }
                    else if (key === 'history')  { setDetailTab('history');  loadHistory(detailTask.id); }
                    else { setDetailTab(key); }
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="p-5 space-y-5">
              {/* Frozen banner */}
              {detailTask.isFrozen && (
                <div className="flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-lg px-4 py-2 text-sm font-medium text-orange-700">
                  <span>This task is frozen</span>
                  {isAdmin && (
                    <button
                      className="ml-auto text-xs bg-orange-100 hover:bg-orange-200 text-orange-700 px-2 py-0.5 rounded transition-colors"
                      onClick={() => handleUnfreezeTask(detailTask)}
                    >
                      Unfreeze
                    </button>
                  )}
                </div>
              )}

              {/* ── INFO TAB ── */}
              {detailTab === 'info' && (
                <div className="space-y-5">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                    <div>
                      <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Partner</p>
                      <p className="font-medium">{detailTask.partner?.staffName || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Manager</p>
                      <p className="font-medium">{detailTask.manager?.staffName || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Category</p>
                      <p>{detailTask.category?.name || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Status</p>
                      <div className="mt-0.5"><StatusDot status={detailTask.status} billing={detailTask.billingStatus} /></div>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Created</p>
                      <p>{detailTask.createdAt ? format(new Date(detailTask.createdAt), 'dd-MMM-yy') : '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Due Date</p>
                      <p className={detailTask.isOverdue ? 'text-red-600 font-medium' : ''}>
                        {detailTask.dueDate ? format(new Date(detailTask.dueDate), 'dd-MMM-yy') : '—'}
                      </p>
                    </div>
                    {detailTask.profitCentre && (
                      <div>
                        <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Profit Centre</p>
                        <p>{detailTask.profitCentre.name}</p>
                      </div>
                    )}
                  </div>

                  {/* ── Assigned Staff ── */}
                  <div className="border border-gray-200 rounded-lg p-3">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Assigned Staff</p>
                    {assignedStaff.length === 0 ? (
                      <p className="text-xs text-gray-400">No staff assigned</p>
                    ) : (
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {assignedStaff.map((entry) => (
                          <span key={entry.id} className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 border border-blue-200 text-xs px-2 py-0.5 rounded-full">
                            {entry.staff.staffName}
                            {canUpdateTask(detailTask) && (
                              <button
                                className="ml-0.5 text-blue-400 hover:text-red-500 font-bold leading-none"
                                onClick={() => handleRemoveTaskStaff(detailTask.id, entry.staffId)}
                                title="Remove"
                              >×</button>
                            )}
                          </span>
                        ))}
                      </div>
                    )}
                    {canUpdateTask(detailTask) && (
                      <div className="space-y-1.5 mt-1">
                        <div className="flex items-center gap-2">
                          <select
                            className="input-field text-xs py-1 flex-1 max-w-xs"
                            value={staffAddId}
                            onChange={(e) => setStaffAddId(e.target.value)}
                          >
                            <option value="">— Add staff member —</option>
                            {activeStaff
                              .filter((s) => !assignedStaff.some((a) => a.staffId === s.id))
                              .map((s) => <option key={s.id} value={s.id}>{s.staffName}</option>)}
                          </select>
                          <button
                            className="btn-primary text-xs py-1 px-3 disabled:opacity-40"
                            disabled={!staffAddId || staffAddLoading}
                            onClick={() => handleAddTaskStaff(detailTask.id)}
                          >Add</button>
                        </div>
                        {/* Bulk-add a partner's entire team in one click — saves
                            hand-picking every reportee when an engagement is
                            taken on by a partner's whole team. */}
                        <div className="flex items-center gap-2">
                          <select
                            className="input-field text-xs py-1 flex-1 max-w-xs"
                            value={teamAddPartnerId}
                            onChange={(e) => setTeamAddPartnerId(e.target.value)}
                          >
                            <option value="">— Add partner's entire team —</option>
                            {partners.map((p) => <option key={p.id} value={p.id}>{p.staffName} (team)</option>)}
                          </select>
                          <button
                            className="btn-primary text-xs py-1 px-3 disabled:opacity-40"
                            disabled={!teamAddPartnerId || teamAddLoading}
                            onClick={() => handleAddTaskTeam(detailTask.id)}
                            title="Adds the partner and every active staff member reporting to them"
                          >Add team</button>
                        </div>
                      </div>
                    )}
                  </div>

                  {canSeeRefTermsForTask(detailTask) && (detailTask.reference || detailTask.terms) && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-2 text-sm">
                      <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Confidential — Partner Only</p>
                      {detailTask.reference && (
                        <div>
                          <p className="text-xs text-amber-600 font-medium mb-0.5">Reference</p>
                          <p className="text-gray-800">{detailTask.reference}</p>
                        </div>
                      )}
                      {detailTask.terms && (
                        <div>
                          <p className="text-xs text-amber-600 font-medium mb-0.5">Terms</p>
                          <p className="text-gray-800 whitespace-pre-wrap">{detailTask.terms}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {canSeeCostForTask(detailTask) && (
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-blue-50 rounded-lg p-3">
                        <p className="text-xs text-gray-500">Cost Incurred</p>
                        <p className="font-bold text-blue-700 text-lg">{detailTask.costIncurred != null ? fmt(detailTask.costIncurred) : '—'}</p>
                      </div>
                      <div className="bg-orange-50 rounded-lg p-3">
                        <p className="text-xs text-gray-500">OPE Incurred</p>
                        <p className="font-bold text-orange-600 text-lg">{detailTask.opeIncurred != null ? fmt(detailTask.opeIncurred) : '—'}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-500">Total</p>
                        <p className="font-bold text-gray-900 text-lg">
                          {detailTask.costIncurred != null && detailTask.opeIncurred != null
                            ? fmt(detailTask.costIncurred + detailTask.opeIncurred) : '—'}
                        </p>
                      </div>
                    </div>
                  )}

                  {canSeeBillingForTask(detailTask) && (detailTask.billedAmount != null || detailTask.billingStatus === 'BILLED') && (
                    <div className="bg-green-50 rounded-lg p-3 text-sm">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs text-green-600 font-medium uppercase tracking-wide">Billing</p>
                        {detailTask.billingStatus === 'UNBILLED' && detailTask.client && (
                          <button
                            className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-2.5 py-1 rounded font-medium transition-colors"
                            onClick={() => navigate(`/invoices?taskId=${detailTask.id}`)}
                          >+ Create Invoice</button>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-4">
                        {detailTask.billedAmount != null && (
                          <div><span className="text-gray-500 text-xs">Billed: </span>
                            <span className="font-bold text-green-700">{fmt(Number(detailTask.billedAmount))}</span></div>
                        )}
                        {detailTask.billingEntity && (
                          <div><span className="text-gray-500 text-xs">Entity: </span>
                            <span className="font-medium">{detailTask.billingEntity.name}</span></div>
                        )}
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${detailTask.billingStatus === 'BILLED' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                          {detailTask.billingStatus}
                        </span>
                      </div>
                      {detailTask.billDetails && <p className="text-gray-600 mt-2 text-xs">{detailTask.billDetails}</p>}
                    </div>
                  )}

                  {detailTask.archiveLink && !isCACertification(detailTask) && (
                    <div className="bg-indigo-50 rounded-lg p-3 text-sm">
                      <p className="text-xs text-indigo-500 font-medium mb-1">Archive</p>
                      <div className="flex flex-wrap items-center gap-2">
                        <a href={detailTask.archiveLink} target="_blank" rel="noreferrer"
                          className="text-indigo-700 hover:underline break-all">{detailTask.archiveLink}</a>
                        {detailTask.archivingConfirmed ? (
                          <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium">✓ Confirmed</span>
                        ) : (
                          <>
                            <span className="text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded-full font-medium">Pending Confirmation</span>
                            {canConfirmArchiving(detailTask) && (
                              <button
                                className="text-xs bg-green-600 hover:bg-green-700 text-white px-2.5 py-0.5 rounded font-medium"
                                onClick={async () => {
                                  try {
                                    await confirmTaskArchive(detailTask.id);
                                    const res = await api.get(`/tasks/${detailTask.id}`);
                                    setDetailTask(res.data); reloadTasks();
                                  } catch (err: any) { alert(err.response?.data?.message || 'Error'); }
                                }}
                              >Confirm Archive</button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {detailTask.status === 'CLOSED' && (detailTask.archivingConfirmed || isCACertification(detailTask)) && !detailTask.isFrozen && (isAdmin || isPartner) && (
                    <div className="flex justify-end">
                      <button
                        className="text-sm bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 px-3 py-1.5 rounded-lg font-medium"
                        onClick={() => handleFreezeTask(detailTask)}
                      >Freeze Task</button>
                    </div>
                  )}
                </div>
              )}

              {/* ── EXPENSES TAB ── */}
              {detailTab === 'expenses' && (
                <div>
                  {detailTask.status === 'CLOSED' && (
                    <p className="text-xs text-amber-700 bg-amber-50 px-3 py-2 rounded-lg mb-3">Task is closed — expenses cannot be added.</p>
                  )}
                  {detailTask.status === 'OPEN' && (
                    <form onSubmit={handleAddExpense} className="grid grid-cols-4 gap-2 mb-3">
                      <input className="input-field col-span-2" placeholder="Description" value={expenseForm.description}
                        onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })} required />
                      <select className="input-field" value={expenseForm.category}
                        onChange={(e) => setExpenseForm({ ...expenseForm, category: e.target.value })}>
                        {EXPENSE_CATEGORIES.map((c) => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
                      </select>
                      <input type="number" className="input-field" placeholder="Amount (₹)" min="0" step="0.01"
                        value={expenseForm.amount} onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })} required />
                      <input type="date" className="input-field" value={expenseForm.date}
                        onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })} required />
                      <button type="submit" className="btn-primary text-sm col-span-3" disabled={expenseSaving}>{expenseSaving ? 'Saving…' : '+ Add Expense'}</button>
                    </form>
                  )}
                  {/* Approved reimbursement claims — claim-wise */}
                  {detailTask.reimbursements && detailTask.reimbursements.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Approved Expense Claims</p>
                      <table className="w-full text-sm">
                        <thead><tr>
                          <th className="table-header">Claim #</th>
                          <th className="table-header">Description</th>
                          <th className="table-header">By</th>
                          <th className="table-header">Date</th>
                          <th className="table-header text-right">Total</th>
                        </tr></thead>
                        <tbody>
                          {detailTask.reimbursements.map((r) => {
                            const total = r.items.reduce((s, i) => s + Number(i.amount), 0);
                            return (
                              <tr key={r.id} className="hover:bg-gray-50">
                                <td className="table-cell font-mono text-xs">{r.claimNumber}</td>
                                <td className="table-cell">{r.notes || '—'}</td>
                                <td className="table-cell text-gray-500 text-xs">{r.staff.staffName}</td>
                                <td className="table-cell text-gray-500 text-xs">{r.approvedAt ? format(new Date(r.approvedAt), 'dd-MMM-yy') : '—'}</td>
                                <td className="table-cell text-right font-medium">{fmt(total)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {/* Direct expenses (non-reimbursement) */}
                  {(() => {
                    const directExp = (detailTask.expenses || []).filter(e => e.category !== 'Reimbursement');
                    return directExp.length > 0 ? (
                      <div>
                        {detailTask.reimbursements && detailTask.reimbursements.length > 0 && (
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Direct Expenses</p>
                        )}
                        <table className="w-full text-sm">
                          <thead><tr>
                            <th className="table-header">Description</th>
                            <th className="table-header">Category</th>
                            <th className="table-header">Date</th>
                            <th className="table-header text-right">Amount</th>
                            <th className="table-header"></th>
                          </tr></thead>
                          <tbody>
                            {directExp.map((exp) => (
                              <tr key={exp.id} className="hover:bg-gray-50">
                                <td className="table-cell">{exp.description}</td>
                                <td className="table-cell text-xs text-gray-500">{exp.category?.replace(/_/g, ' ')}</td>
                                <td className="table-cell text-gray-500">{exp.date?.slice(0, 10)}</td>
                                <td className="table-cell text-right font-medium">{fmt(Number(exp.amount))}</td>
                                <td className="table-cell">
                                  {detailTask.status === 'OPEN' && (
                                    <button className="text-red-500 hover:text-red-700 text-xs"
                                      onClick={() => handleDeleteExpense(exp.id)}>Delete</button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (!detailTask.reimbursements?.length && <p className="text-gray-400 text-sm">No expenses yet.</p>);
                  })()}
                </div>
              )}

              {/* ── DOCUMENTS TAB ── */}
              {detailTab === 'documents' && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-gray-800">Documents</h4>
                    <div>
                      <input ref={fileRef} type="file" className="hidden" id="doc-upload" onChange={handleUploadDoc}
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" />
                      <label htmlFor="doc-upload" className={`btn-secondary text-sm cursor-pointer ${uploadingDoc ? 'opacity-50' : ''}`}>
                        {uploadingDoc ? 'Uploading...' : '+ Upload File'}
                      </label>
                    </div>
                  </div>
                  {detailTask.documents && detailTask.documents.length > 0 ? (
                    <div className="space-y-2">
                      {detailTask.documents.map((doc: Document) => (
                        <div key={doc.id} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                          <span className="text-xl">{doc.mimeType === 'application/pdf' ? '📄' : doc.mimeType.startsWith('image') ? '🖼️' : '📎'}</span>
                          <div className="flex-1 min-w-0">
                            <a href={uploadsUrl(doc.fileName)} target="_blank" rel="noreferrer"
                              className="text-sm font-medium text-blue-600 hover:underline truncate block">{doc.originalName}</a>
                            <p className="text-xs text-gray-400">{fileSizeStr(doc.fileSize)} · {format(new Date(doc.createdAt), 'dd-MMM-yy')}</p>
                          </div>
                          <button className="text-red-500 hover:text-red-700 text-xs" onClick={() => handleDeleteDoc(doc.id)}>Delete</button>
                        </div>
                      ))}
                    </div>
                  ) : <p className="text-gray-400 text-sm">No documents attached.</p>}
                </div>
              )}

              {/* ── SUB-TASKS TAB ── */}
              {detailTab === 'subtasks' && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-gray-800">Sub-Tasks</h4>
                    <button className="btn-primary text-sm" onClick={() => { setEditingSubTask(null); setSubTaskForm({ name: '', description: '', assignedToId: '', dueDate: '' }); setShowSubTaskForm(true); }}>
                      + Add Sub-Task
                    </button>
                  </div>

                  {/* Sub-task create/edit form */}
                  {showSubTaskForm && (
                    <form onSubmit={handleSubTaskSubmit} className="bg-gray-50 rounded-lg p-4 mb-4 space-y-3">
                      <h5 className="text-sm font-semibold text-gray-700">{editingSubTask ? 'Edit Sub-Task' : 'New Sub-Task'}</h5>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2">
                          <label className="label">Name <span className="text-red-500">*</span></label>
                          <input className="input-field" value={subTaskForm.name}
                            onChange={(e) => setSubTaskForm({ ...subTaskForm, name: e.target.value })} required />
                        </div>
                        <div className="col-span-2">
                          <label className="label">Description</label>
                          <textarea className="input-field" rows={2} value={subTaskForm.description}
                            onChange={(e) => setSubTaskForm({ ...subTaskForm, description: e.target.value })} />
                        </div>
                        <div>
                          <label className="label">Assign To</label>
                          <select className="input-field" value={subTaskForm.assignedToId}
                            onChange={(e) => setSubTaskForm({ ...subTaskForm, assignedToId: e.target.value })}>
                            <option value="">— None —</option>
                            {activeStaff.map((s) => <option key={s.id} value={s.id}>{s.staffName}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="label">Due Date</label>
                          <input type="date" className="input-field" value={subTaskForm.dueDate}
                            onChange={(e) => setSubTaskForm({ ...subTaskForm, dueDate: e.target.value })} />
                        </div>
                      </div>
                      <div className="flex gap-2 justify-end">
                        <button type="button" className="btn-secondary text-sm" disabled={subTaskSaving} onClick={() => { setShowSubTaskForm(false); setEditingSubTask(null); }}>Cancel</button>
                        <button type="submit" className="btn-primary text-sm" disabled={subTaskSaving}>
                          {subTaskSaving ? 'Saving…' : editingSubTask ? 'Update' : 'Create'}
                        </button>
                      </div>
                    </form>
                  )}

                  {subTasksLoading ? (
                    <p className="text-gray-400 text-sm">Loading...</p>
                  ) : subTasks.length === 0 ? (
                    <p className="text-gray-400 text-sm">No sub-tasks yet.</p>
                  ) : (
                    <div className="space-y-2">
                      {subTasks.map((st) => (
                        <div key={st.id} className="border border-gray-200 rounded-lg p-3 bg-white">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-mono text-xs text-blue-600 font-medium">{st.subTaskNumber}</span>
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                  st.status === 'CLOSED' ? 'bg-gray-100 text-gray-500' :
                                  st.status === 'SENT_FOR_REVIEW' ? 'bg-amber-100 text-amber-700' :
                                  'bg-green-100 text-green-700'
                                }`}>{st.status.replace(/_/g, ' ')}</span>
                                {st.assignedTo && <span className="text-xs text-gray-500">→ {st.assignedTo.staffName}</span>}
                                {st.createdAt && <span className="text-xs text-gray-400">Created: {format(new Date(st.createdAt), 'dd-MMM-yy')}</span>}
                                {st.dueDate && <span className="text-xs text-gray-400">Due: {format(new Date(st.dueDate), 'dd-MMM-yy')}</span>}
                              </div>
                              <p className="text-sm font-medium text-gray-800 mt-1">{st.name}</p>
                              {st.description && <p className="text-xs text-gray-500 mt-0.5">{st.description}</p>}
                            </div>
                            <div className="flex gap-2 items-center shrink-0">
                              {st.status !== 'CLOSED' && (
                                <>
                                  {st.status === 'OPEN' && (
                                    <button
                                      className="text-xs text-amber-600 hover:text-amber-800 font-medium"
                                      onClick={() => handleSubTaskStatusChange(st, 'SENT_FOR_REVIEW')}
                                    >Send for Review</button>
                                  )}
                                  {(isAdmin || user?.staffId === detailTask.partner?.id || (detailTask.partner as any)?.reportingPartnerId === user?.staffId) && (
                                    <button
                                      className="text-xs text-gray-600 hover:text-gray-800 font-medium"
                                      onClick={() => handleSubTaskStatusChange(st, 'CLOSED')}
                                    >Close</button>
                                  )}
                                  <button
                                    className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                                    onClick={() => openEditSubTask(st)}
                                  >Edit</button>
                                </>
                              )}
                              {(isAdmin || user?.staffId === detailTask.partner?.id) && (
                                <button
                                  className="text-xs text-red-500 hover:text-red-700"
                                  onClick={() => handleDeleteSubTask(st.id)}
                                >Del</button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── CERTIFICATES TAB ── */}
              {detailTab === 'certificates' && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-gray-800">Certificates</h4>
                    {canUpdateTask(detailTask) && (
                      <button className="btn-primary text-sm" onClick={() => {
                        setEditingCert(null);
                        setCertForm({ name: '', description: '', assignedToId: '', dueDate: '' });
                        setShowCertForm(true);
                      }}>+ Add Certificate</button>
                    )}
                  </div>

                  {showCertForm && (
                    <form onSubmit={handleCertSubmit} className="bg-gray-50 rounded-lg p-4 mb-4 space-y-3">
                      <h5 className="text-sm font-semibold text-gray-700">{editingCert ? 'Edit Certificate' : 'New Certificate'}</h5>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2">
                          <label className="label">Name <span className="text-red-500">*</span></label>
                          <input className="input-field" value={certForm.name}
                            onChange={(e) => setCertForm({ ...certForm, name: e.target.value })} required />
                        </div>
                        <div className="col-span-2">
                          <label className="label">Description</label>
                          <textarea className="input-field" rows={2} value={certForm.description}
                            onChange={(e) => setCertForm({ ...certForm, description: e.target.value })} />
                        </div>
                        <div>
                          <label className="label">Assign To</label>
                          <select className="input-field" value={certForm.assignedToId}
                            onChange={(e) => setCertForm({ ...certForm, assignedToId: e.target.value })}>
                            <option value="">— None —</option>
                            {activeStaff.map((s) => <option key={s.id} value={s.id}>{s.staffName}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="label">Due Date</label>
                          <input type="date" className="input-field" value={certForm.dueDate}
                            onChange={(e) => setCertForm({ ...certForm, dueDate: e.target.value })} />
                        </div>
                      </div>
                      <div className="flex gap-2 justify-end">
                        <button type="button" className="btn-secondary text-sm" disabled={certSaving} onClick={() => { setShowCertForm(false); setEditingCert(null); }}>Cancel</button>
                        <button type="submit" className="btn-primary text-sm" disabled={certSaving}>
                          {certSaving ? 'Saving…' : editingCert ? 'Update' : 'Create'}
                        </button>
                      </div>
                    </form>
                  )}

                  {certsLoading ? (
                    <p className="text-gray-400 text-sm">Loading...</p>
                  ) : certificates.length === 0 ? (
                    <p className="text-gray-400 text-sm">No certificates yet.</p>
                  ) : (
                    <div className="space-y-2">
                      {certificates.map((cert) => (
                        <div key={cert.id} className="border border-gray-200 rounded-lg p-3 bg-white">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-mono text-xs text-blue-600 font-medium">{cert.certificateNumber}</span>
                                {cert.assignedTo && <span className="text-xs text-gray-500">→ {cert.assignedTo.staffName}</span>}
                                {cert.createdAt && <span className="text-xs text-gray-400">Created: {format(new Date(cert.createdAt), 'dd-MMM-yy')}</span>}
                                {cert.dueDate && <span className="text-xs text-gray-400">Due: {format(new Date(cert.dueDate), 'dd-MMM-yy')}</span>}
                                {cert.udin && (
                                  <span className="text-xs bg-green-50 text-green-700 border border-green-200 px-1.5 py-0.5 rounded font-mono">UDIN ✓</span>
                                )}
                                {cert.archiveLink && (
                                  <span className="text-xs bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded">🔗 Archived</span>
                                )}
                                {cert.fileName && (
                                  <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">📄 File</span>
                                )}
                                {cert.invoice && (
                                  <span className="text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded font-medium">
                                    {cert.invoice.invoiceNumber}
                                  </span>
                                )}
                              </div>
                              <p className="text-sm font-medium text-gray-800 mt-1">{cert.name}</p>
                              {cert.description && <p className="text-xs text-gray-500 mt-0.5">{cert.description}</p>}
                            </div>
                            <div className="flex gap-2 items-center shrink-0">
                              {canUpdateTask(detailTask) && (
                                <button className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                                  onClick={() => {
                                    setEditingCert(cert);
                                    setCertForm({
                                      name: cert.name,
                                      description: cert.description || '',
                                      assignedToId: cert.assignedTo ? String(cert.assignedTo.id) : '',
                                      dueDate: cert.dueDate ? cert.dueDate.slice(0, 10) : '',
                                    });
                                    setShowCertForm(true);
                                  }}>Edit</button>
                              )}
                              {(isAdmin || user?.staffId === detailTask.partner?.id) && (
                                <button className="text-xs text-red-500 hover:text-red-700"
                                  onClick={() => handleDeleteCert(cert.id)}>Del</button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── LITIGATIONS TAB ── */}
              {detailTab === 'litigations' && (
                <LitigationsPanel taskId={detailTask.id} canEdit={!detailTask.isFrozen} />
              )}

              {/* ── COMPLIANCES TAB ── */}
              {detailTab === 'compliances' && (() => {
                const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                const curYear = new Date().getFullYear();
                const yearOpts = Array.from({ length: 8 }, (_, i) => curYear - 2 + i);
                return (
                <div>
                  {/* Enrolled types section */}
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-gray-800">Added Compliances</h4>
                    <div className="flex gap-2">
                      {canUpdateTask(detailTask) && (
                        <button className="btn-primary text-sm" onClick={() => { setShowEnrollForm(true); setEnrollForm({ taxId: '', taxIdSource: 'custom', complianceTypeId: '', startMonth: '', startYear: '', endMonth: '', endYear: '' }); }}>
                          + Add Compliance
                        </button>
                      )}
                    </div>
                  </div>

                  {showEnrollForm && (
                    <form onSubmit={handleEnrollSubmit} className="bg-gray-50 rounded-lg p-4 mb-4 space-y-3">
                      <h5 className="text-sm font-semibold text-gray-700">Add Compliance</h5>
                      <div>
                        <label className="label">Tax ID <span className="text-red-500">*</span></label>
                        <div className="space-y-1.5">
                          {clientTaxOptions.length > 0 && (
                            <select
                              className="input-field"
                              value={enrollForm.taxIdSource === 'custom' ? '__custom__' : enrollForm.taxId}
                              onChange={(e) => {
                                const v = e.target.value;
                                if (v === '__custom__' || v === '') {
                                  setEnrollForm({ ...enrollForm, taxIdSource: 'custom', taxId: '' });
                                } else {
                                  setEnrollForm({ ...enrollForm, taxIdSource: 'picked', taxId: v });
                                }
                              }}>
                              <option value="">— Pick from client&apos;s saved IDs —</option>
                              {clientTaxOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                              <option value="__custom__">+ Enter custom Tax ID…</option>
                            </select>
                          )}
                          {(clientTaxOptions.length === 0 || enrollForm.taxIdSource === 'custom') && (
                            <input
                              className="input-field font-mono uppercase"
                              placeholder="e.g. AAACK6337K1ZJ"
                              value={enrollForm.taxId}
                              onChange={(e) => setEnrollForm({ ...enrollForm, taxId: e.target.value.toUpperCase(), taxIdSource: 'custom' })}
                              required
                            />
                          )}
                          <p className="text-xs text-gray-500">PAN / GSTIN / TAN — used to check filing status from the government portal</p>
                        </div>
                      </div>
                      <div>
                        <label className="label">Compliance Type <span className="text-red-500">*</span></label>
                        <select className="input-field" value={enrollForm.complianceTypeId}
                          onChange={(e) => setEnrollForm({ ...enrollForm, complianceTypeId: e.target.value })}
                          required disabled={!enrollForm.taxId.trim()}>
                          <option value="">{enrollForm.taxId.trim() ? '— Select —' : '— Enter Tax ID first —'}</option>
                          {/* Only hide a type if it's already enrolled for THIS tax ID, so a
                              client with two GSTINs can enrol the same type once per GSTIN. */}
                          {complianceTypes.filter(ct => ct.isActive !== false && !enrollments.find(en => en.complianceTypeId === ct.id && (en.taxId || '').trim() === enrollForm.taxId.trim()))
                            .map(ct => <option key={ct.id} value={ct.id}>{ct.name} ({ct.frequency})</option>)}
                        </select>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="label">Start Month/Year <span className="text-red-500">*</span></label>
                          <div className="flex gap-1.5">
                            <select className="input-field flex-1" value={enrollForm.startMonth}
                              onChange={(e) => setEnrollForm({ ...enrollForm, startMonth: e.target.value })} required>
                              <option value="">Month</option>
                              {months.map((m, i) => <option key={i} value={String(i+1).padStart(2,'0')}>{m}</option>)}
                            </select>
                            <select className="input-field w-24" value={enrollForm.startYear}
                              onChange={(e) => setEnrollForm({ ...enrollForm, startYear: e.target.value })} required>
                              <option value="">Year</option>
                              {yearOpts.map(y => <option key={y} value={y}>{y}</option>)}
                            </select>
                          </div>
                        </div>
                        <div>
                          <label className="label">End Month/Year <span className="text-gray-400">(optional)</span></label>
                          <div className="flex gap-1.5">
                            <select className="input-field flex-1" value={enrollForm.endMonth}
                              onChange={(e) => setEnrollForm({ ...enrollForm, endMonth: e.target.value })}>
                              <option value="">Month</option>
                              {months.map((m, i) => <option key={i} value={String(i+1).padStart(2,'0')}>{m}</option>)}
                            </select>
                            <select className="input-field w-24" value={enrollForm.endYear}
                              onChange={(e) => setEnrollForm({ ...enrollForm, endYear: e.target.value })}>
                              <option value="">Year</option>
                              {yearOpts.map(y => <option key={y} value={y}>{y}</option>)}
                            </select>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 justify-end">
                        <button type="button" className="btn-secondary text-sm" disabled={enrollSaving} onClick={() => setShowEnrollForm(false)}>Cancel</button>
                        <button type="submit" className="btn-primary text-sm" disabled={enrollSaving}>{enrollSaving ? 'Saving…' : 'Add'}</button>
                      </div>
                    </form>
                  )}

                  {enrollments.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {enrollments.map(e => (
                        <div key={e.id} className="inline-flex items-center gap-1.5 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-1.5">
                          <span className="text-xs font-medium text-indigo-800">{e.complianceType.name}</span>
                          <span className="text-xs text-indigo-500">({e.complianceType.frequency})</span>
                          {e.taxId && <span className="text-xs font-mono text-indigo-600 bg-white border border-indigo-100 rounded px-1.5 py-0.5" title="Tax ID">{e.taxId}</span>}
                          <span className="text-xs text-gray-400">from {e.startPeriod?.slice(0, 7)}</span>
                          {e.endPeriod && <span className="text-xs text-gray-400">to {e.endPeriod?.slice(0, 7)}</span>}
                          {canUpdateTask(detailTask) && (
                            <button className="text-red-400 hover:text-red-600 text-xs ml-1"
                              onClick={() => handleRemoveEnrollment(e.id)} title="Remove">✕</button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Compliance instances */}
                  <h4 className="font-semibold text-gray-800 mb-2 mt-4">Compliance Instances</h4>
                  {compliancesLoading ? (
                    <p className="text-gray-400 text-sm">Loading...</p>
                  ) : complianceInstances.length === 0 ? (
                    <p className="text-gray-400 text-sm">No compliance instances yet. Add compliance types above — instances are generated automatically on the 1st of each month.</p>
                  ) : (
                    <div className="space-y-2">
                      {complianceInstances.map(comp => {
                        const effectiveDue = new Date(comp.extendedDueDate || comp.dueDate);
                        const todayD = new Date(); todayD.setHours(0,0,0,0); effectiveDue.setHours(0,0,0,0);
                        const escalation = new Date(effectiveDue); escalation.setDate(escalation.getDate() - 1);
                        const diff = Math.ceil((effectiveDue.getTime() - todayD.getTime()) / (1000*60*60*24));
                        const isOverdue = comp.status !== 'COMPLETED' && diff < 0;
                        const isEscalated = comp.status !== 'COMPLETED' && todayD >= escalation && diff >= 0;
                        return (
                          <div key={comp.id} className={`border rounded-lg p-3 bg-white ${isOverdue ? 'border-red-300 bg-red-50/50' : isEscalated ? 'border-amber-300 bg-amber-50/30' : 'border-gray-200'}`}>
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-mono text-xs text-blue-600 font-medium">{comp.complianceNumber}</span>
                                  <span className="text-xs font-medium text-gray-700">{comp.complianceType.name}</span>
                                  <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{comp.periodLabel}</span>
                                  {comp.taxId && <span className="text-xs font-mono text-indigo-600 bg-indigo-50 border border-indigo-100 px-1.5 py-0.5 rounded" title="GSTIN / Tax ID">{comp.taxId}</span>}
                                  {comp.fileName && <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">📄 File</span>}
                                </div>
                                <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                                  <span className="text-xs text-gray-500">
                                    Due: {(comp.extendedDueDate || comp.dueDate)?.slice(0, 10)}
                                    {comp.extendedDueDate && <span className="text-purple-600 ml-1" title={`Original: ${comp.dueDate?.slice(0,10)}`}>(extended)</span>}
                                  </span>
                                  {isOverdue && <span className="text-xs text-red-600 font-semibold">{Math.abs(diff)}d overdue</span>}
                                  {isEscalated && <span className="text-xs text-red-600 font-medium">{diff === 0 ? 'Due today' : `${diff}d left`}</span>}
                                  {!isOverdue && !isEscalated && diff > 0 && diff <= 3 && comp.status !== 'COMPLETED' && <span className="text-xs text-amber-600 font-medium">{diff}d left</span>}
                                  {comp.remarks && <span className="text-xs text-gray-400 italic truncate max-w-[200px]" title={comp.remarks}>{comp.remarks}</span>}
                                </div>
                              </div>
                              <div className="flex gap-2 items-center shrink-0">
                                {canUpdateTask(detailTask) && comp.status !== 'COMPLETED' ? (
                                  <select
                                    className={`text-xs px-2 py-1 rounded-full border font-medium ${
                                      comp.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800 border-yellow-200' :
                                      comp.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-800 border-blue-200' :
                                      comp.status === 'OVERDUE' ? 'bg-red-100 text-red-800 border-red-200' :
                                      'bg-green-100 text-green-700 border-green-200'
                                    }`}
                                    value={comp.status}
                                    onChange={(e) => handleComplianceStatusChange(comp.id, e.target.value)}
                                  >
                                    <option value="PENDING">Pending</option>
                                    <option value="IN_PROGRESS">In Progress</option>
                                    <option value="COMPLETED">Completed</option>
                                  </select>
                                ) : (
                                  <span className={`text-xs px-2 py-1 rounded-full border font-medium ${
                                    comp.status === 'COMPLETED' ? 'bg-green-100 text-green-700 border-green-200' :
                                    comp.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800 border-yellow-200' :
                                    'bg-blue-100 text-blue-800 border-blue-200'
                                  }`}>{comp.status.replace(/_/g, ' ')}</span>
                                )}
                                {(isAdmin || user?.staffId === detailTask.partner?.id) && (
                                  <button className="text-xs text-red-500 hover:text-red-700"
                                    onClick={() => handleDeleteCompliance(comp.id)}>Del</button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                );
              })()}

              {/* ── COMMENTS TAB ── */}
              {detailTab === 'comments' && (
                <div className="space-y-4">
                  {/* Comment form */}
                  {user?.staffId && (
                    <form onSubmit={handleCommentSubmit} className="flex gap-2">
                      <textarea
                        className="input-field flex-1 resize-none text-sm"
                        rows={2}
                        placeholder="Write a comment..."
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleCommentSubmit(e as any); } }}
                      />
                      <button
                        type="submit"
                        disabled={!commentText.trim() || commentSubmitting}
                        className="btn-primary text-sm self-end disabled:opacity-50"
                      >
                        {commentSubmitting ? '...' : 'Post'}
                      </button>
                    </form>
                  )}

                  {/* Comments list */}
                  {commentsLoading ? (
                    <p className="text-gray-400 text-sm text-center py-4">Loading comments...</p>
                  ) : comments.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      <p className="text-2xl mb-2">💬</p>
                      <p className="text-sm">No comments yet. Be the first to comment.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {comments.map((c) => (
                        <div key={c.id} className="flex gap-3">
                          <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-bold shrink-0">
                            {c.author.staffName.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 bg-gray-50 rounded-xl px-4 py-3">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-semibold text-gray-800">{c.author.staffName}</span>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-400">
                                  {format(new Date(c.createdAt), 'dd-MMM-yy HH:mm')}
                                </span>
                                {(isAdmin || user?.staffId === c.author.id) && (
                                  <button
                                    onClick={() => handleDeleteComment(c.id)}
                                    className="text-xs text-red-400 hover:text-red-600"
                                    title="Delete comment"
                                  >✕</button>
                                )}
                              </div>
                            </div>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap">{c.content}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── HISTORY TAB ── */}
              {detailTab === 'history' && (
                <div>
                  {historyLoading ? (
                    <p className="text-gray-400 text-sm text-center py-4">Loading history...</p>
                  ) : auditHistory.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      <p className="text-2xl mb-2">📜</p>
                      <p className="text-sm">No history recorded yet.</p>
                    </div>
                  ) : (
                    <div className="relative">
                      {/* Timeline */}
                      <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />
                      <div className="space-y-4 pl-10">
                        {auditHistory.map((entry) => {
                          const actor = entry.user.staff?.staffName ?? entry.user.email;
                          const actionColor =
                            entry.action === 'CREATE' ? 'bg-green-100 text-green-700' :
                            entry.action === 'DELETE' ? 'bg-red-100 text-red-700' :
                            entry.action === 'CLOSE'  ? 'bg-gray-100 text-gray-700' :
                            'bg-blue-100 text-blue-700';
                          return (
                            <div key={entry.id} className="relative">
                              <div className="absolute -left-7 w-3 h-3 rounded-full bg-blue-400 border-2 border-white top-1.5" />
                              <div className="bg-gray-50 rounded-xl px-4 py-3">
                                <div className="flex items-center justify-between mb-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-sm font-semibold text-gray-800">{actor}</span>
                                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${actionColor}`}>{entry.action}</span>
                                    <span className="text-xs text-gray-500">{entry.entity}</span>
                                  </div>
                                  <span className="text-xs text-gray-400 shrink-0 ml-2">
                                    {format(new Date(entry.createdAt), 'dd-MMM-yy HH:mm')}
                                  </span>
                                </div>
                                {entry.changes && typeof entry.changes === 'object' && Object.keys(entry.changes).length > 0 && (
                                  <details className="mt-1">
                                    <summary className="text-xs text-blue-500 cursor-pointer hover:underline">View changes</summary>
                                    <pre className="text-xs bg-white border border-gray-100 rounded p-2 mt-1 overflow-x-auto text-gray-600 max-h-40">
                                      {JSON.stringify(entry.changes, null, 2)}
                                    </pre>
                                  </details>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── TEMPLATE MANAGER MODAL ── */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Task Templates</h3>
              <button className="text-gray-400 hover:text-gray-600 text-xl" onClick={() => setShowTemplateModal(false)}>✕</button>
            </div>

            {/* Existing templates list */}
            {!showTemplateForm && (
              <div className="mb-6">
                {templates.length === 0 ? (
                  <p className="text-gray-400 text-sm text-center py-4">No templates yet. Create one below.</p>
                ) : (
                  <div className="space-y-2 mb-4">
                    {templates.map((t) => (
                      <div key={t.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="text-sm font-medium text-gray-800">{t.name}</p>
                          {t.description && <p className="text-xs text-gray-500 mt-0.5">{t.description}</p>}
                          {t.checklist?.length > 0 && (
                            <p className="text-xs text-blue-600 mt-0.5">{t.checklist.length} checklist item{t.checklist.length !== 1 ? 's' : ''}</p>
                          )}
                        </div>
                        <div className="flex gap-2 ml-3">
                          <button className="text-blue-600 hover:text-blue-800 text-xs font-medium" onClick={() => openEditTemplate(t)}>Edit</button>
                          <button className="text-red-600 hover:text-red-800 text-xs font-medium" onClick={() => handleDeleteTemplate(t.id)}>Delete</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <button className="btn-primary text-sm" onClick={() => { setEditingTemplate(null); setTemplateForm({ name: '', description: '', categoryId: '', checklist: '' }); setShowTemplateForm(true); }}>
                  + New Template
                </button>
              </div>
            )}

            {/* Create / Edit form */}
            {showTemplateForm && (
              <form onSubmit={handleSaveTemplate} className="space-y-4 border-t pt-4">
                <h4 className="text-sm font-semibold text-gray-700">{editingTemplate ? `Edit: ${editingTemplate.name}` : 'New Template'}</h4>
                <div>
                  <label className="label">Template Name <span className="text-red-500">*</span></label>
                  <input className="input-field" value={templateForm.name} required
                    onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })} />
                </div>
                <div>
                  <label className="label">Description</label>
                  <input className="input-field" value={templateForm.description}
                    onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })} />
                </div>
                <div>
                  <label className="label">Default Category</label>
                  <select className="input-field" value={templateForm.categoryId}
                    onChange={(e) => setTemplateForm({ ...templateForm, categoryId: e.target.value })}>
                    <option value="">— None —</option>
                    {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Checklist (one item per line)</label>
                  <textarea className="input-field" rows={5} value={templateForm.checklist}
                    placeholder={"Review documents\nPrepare report\nClient sign-off"}
                    onChange={(e) => setTemplateForm({ ...templateForm, checklist: e.target.value })} />
                </div>
                <div className="flex gap-3 justify-end">
                  <button type="button" className="btn-secondary" disabled={templateSaving} onClick={() => { setShowTemplateForm(false); setEditingTemplate(null); }}>Cancel</button>
                  <button type="submit" className="btn-primary" disabled={templateSaving}>
                    {templateSaving ? 'Saving…' : editingTemplate ? 'Update' : 'Create'} Template
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* ── BULK OPERATIONS MODAL ── */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Bulk Actions ({selectedTaskIds.length} tasks)</h3>
              <button className="text-gray-400 hover:text-gray-600 text-xl" onClick={() => setShowBulkModal(false)}>✕</button>
            </div>

            {bulkResult ? (
              <div className="space-y-3">
                <div className="flex gap-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 flex-1 text-center">
                    <p className="text-2xl font-bold text-green-600">{bulkResult.succeeded}</p>
                    <p className="text-xs text-green-600 font-medium">Succeeded</p>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex-1 text-center">
                    <p className="text-2xl font-bold text-red-600">{bulkResult.failed}</p>
                    <p className="text-xs text-red-600 font-medium">Failed / Skipped</p>
                  </div>
                </div>
                {bulkResult.results.filter((r: any) => !r.success).length > 0 && (
                  <div className="text-xs text-red-600 bg-red-50 rounded-lg p-3 space-y-1">
                    {bulkResult.results.filter((r: any) => !r.success).map((r: any, i: number) => (
                      <p key={i}>Task #{r.taskId}: {r.error}</p>
                    ))}
                  </div>
                )}
                <div className="flex gap-3 justify-end pt-2">
                  <button className="btn-secondary" onClick={() => { setShowBulkModal(false); setSelectedTaskIds([]); setBulkResult(null); }}>Done</button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="label">Action</label>
                  <select className="input-field" value={bulkAction}
                    onChange={(e) => setBulkAction(e.target.value as any)}>
                    <option value="CLOSE">Close Tasks (requires archive link)</option>
                    <option value="REASSIGN_MANAGER">Reassign Manager</option>
                    <option value="REASSIGN_PARTNER">Reassign Partner</option>
                    <option value="ASSIGN_STAFF">Assign Staff Member</option>
                  </select>
                </div>
                {bulkAction !== 'CLOSE' && (
                  <div>
                    <label className="label">
                      {bulkAction === 'REASSIGN_MANAGER' ? 'New Manager'
                        : bulkAction === 'REASSIGN_PARTNER' ? 'New Partner'
                        : 'Staff Member to Add'}
                    </label>
                    <select className="input-field" value={bulkStaffId}
                      onChange={(e) => setBulkStaffId(e.target.value)} required>
                      <option value="">— Select staff —</option>
                      {(bulkAction === 'REASSIGN_PARTNER' ? partners : activeStaff).map((s) => (
                        <option key={s.id} value={s.id}>{s.staffName}</option>
                      ))}
                    </select>
                  </div>
                )}
                <p className="text-xs text-gray-500 bg-gray-50 rounded-lg p-3">
                  ⚠ Only tasks where you are the assigned Partner or their Reporting Partner will be updated. Others will be skipped.
                </p>
                <div className="flex gap-3 justify-end pt-2">
                  <button className="btn-secondary" onClick={() => setShowBulkModal(false)}>Cancel</button>
                  <button className="btn-primary" onClick={handleBulkSubmit} disabled={bulkLoading || (bulkAction !== 'CLOSE' && !bulkStaffId)}>
                    {bulkLoading ? 'Processing...' : `Apply to ${selectedTaskIds.length} tasks`}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Sub-Tasks listing (page-level tab) ──────────────────────────────────────
const SubTasksList: React.FC = () => {
  const { user, isAdmin, isPartner } = useAuth();

  interface SubTaskRow {
    id: number;
    subTaskNumber: string;
    name: string;
    description?: string;
    status: 'OPEN' | 'SENT_FOR_REVIEW' | 'CLOSED';
    dueDate?: string;
    createdAt: string;
    assignedTo?: { id: number; staffName: string };
    task: { id: number; taskId: string; taskName: string; client?: { clientName: string }; partner?: { id: number } };
  }

  const [subTasks, setSubTasks] = useState<SubTaskRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [filterAssignedTo, setFilterAssignedTo] = useState('');
  const [filterSubCreatedFrom, setFilterSubCreatedFrom] = useState('');
  const [filterSubCreatedTo, setFilterSubCreatedTo] = useState('');
  const [filterSubDueFrom, setFilterSubDueFrom] = useState('');
  const [filterSubDueTo, setFilterSubDueTo] = useState('');
  const [editingRow, setEditingRow] = useState<SubTaskRow | null>(null);
  const [editForm, setEditForm] = useState({ name: '', description: '', assignedToId: '', dueDate: '' });
  const [staff, setStaff] = useState<{ id: number; staffName: string; isActive?: boolean }[]>([]);

  const fetchAll = async () => {
    setLoading(true);
    const [stRes, sRes] = await Promise.all([getAllSubTasks(), getStaff()]);
    setSubTasks(stRes.data);
    setStaff(sRes.data);
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  const filtered = subTasks.filter((st) => {
    const q = search.toLowerCase();
    const matchSearch =
      st.subTaskNumber.toLowerCase().includes(q) ||
      st.name.toLowerCase().includes(q) ||
      st.task.taskName.toLowerCase().includes(q) ||
      st.task.taskId.toLowerCase().includes(q) ||
      (st.task.client?.clientName || '').toLowerCase().includes(q) ||
      (st.assignedTo?.staffName || '').toLowerCase().includes(q);
    const matchStatus = filterStatus ? st.status === filterStatus : true;
    const matchClient = filterClient ? (st.task.client?.clientName || '') === filterClient : true;
    const matchAssigned = filterAssignedTo ? (st.assignedTo?.staffName || '') === filterAssignedTo : true;
    const createdYmd = st.createdAt ? String(st.createdAt).slice(0, 10) : '';
    const dueYmd = st.dueDate ? String(st.dueDate).slice(0, 10) : '';
    const matchSubCreatedFrom = filterSubCreatedFrom ? createdYmd >= filterSubCreatedFrom : true;
    const matchSubCreatedTo = filterSubCreatedTo ? createdYmd <= filterSubCreatedTo : true;
    const matchSubDueFrom = filterSubDueFrom ? dueYmd >= filterSubDueFrom : true;
    const matchSubDueTo = filterSubDueTo ? dueYmd <= filterSubDueTo : true;
    return matchSearch && matchStatus && matchClient && matchAssigned &&
      matchSubCreatedFrom && matchSubCreatedTo && matchSubDueFrom && matchSubDueTo;
  });

  const clientFilterOptions: FilterOption[] = React.useMemo(() => {
    const names = Array.from(new Set(subTasks.map(st => st.task.client?.clientName).filter(Boolean))) as string[];
    return names.sort().map(n => ({ value: n, label: n }));
  }, [subTasks]);

  const assignedToOptions: FilterOption[] = React.useMemo(() => {
    const names = Array.from(new Set(subTasks.map(st => st.assignedTo?.staffName).filter(Boolean))) as string[];
    return names.sort().map(n => ({ value: n, label: n }));
  }, [subTasks]);

  const statusBadge = (s: string) => {
    if (s === 'OPEN') return 'bg-green-100 text-green-700';
    if (s === 'SENT_FOR_REVIEW') return 'bg-amber-100 text-amber-700';
    return 'bg-gray-100 text-gray-500';
  };

  const canClose = (st: SubTaskRow) =>
    isAdmin || user?.staffId === (st.task as any).partnerId ||
    (st.task as any).partner?.reportingPartnerId === user?.staffId;

  const canDelete = (st: SubTaskRow) =>
    isAdmin || user?.staffId === (st.task as any).partnerId;

  const handleStatusChange = async (st: SubTaskRow, newStatus: SubTaskRow['status']) => {
    try {
      if (newStatus === 'CLOSED') {
        await closeSubTaskApi(st.id);
      } else {
        await updateSubTaskApi(st.id, { status: newStatus });
      }
      fetchAll();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error updating sub-task');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this sub-task?')) return;
    try {
      await deleteSubTaskApi(id);
      fetchAll();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error deleting sub-task');
    }
  };

  const openEdit = (st: SubTaskRow) => {
    setEditingRow(st);
    setEditForm({
      name: st.name,
      description: st.description || '',
      assignedToId: st.assignedTo?.id ? String(st.assignedTo.id) : '',
      dueDate: st.dueDate ? st.dueDate.slice(0, 10) : '',
    });
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingRow) return;
    try {
      await updateSubTaskApi(editingRow.id, {
        name: editForm.name,
        description: editForm.description || null,
        assignedToId: editForm.assignedToId || null,
        dueDate: editForm.dueDate || null,
      });
      setEditingRow(null);
      fetchAll();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error saving sub-task');
    }
  };

  const activeStaff = staff.filter((s: any) => s.isActive !== false);

  const [subSortField, setSubSortField] = useState<'subTaskNumber' | 'name' | 'task' | 'client' | 'assignedTo' | 'createdAt' | 'dueDate' | 'status'>('subTaskNumber');
  const [subSortDir, setSubSortDir] = useState<'asc' | 'desc'>('asc');
  const handleSubSort = (f: typeof subSortField) => {
    if (subSortField === f) setSubSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSubSortField(f); setSubSortDir('asc'); }
  };
  const SubSI = ({ f }: { f: typeof subSortField }) =>
    subSortField !== f ? <span className="ml-1 text-gray-300">↕</span>
    : <span className="ml-1 text-blue-600">{subSortDir === 'asc' ? '↑' : '↓'}</span>;

  const sortedSubs = [...filtered].sort((a, b) => {
    let av = '', bv = '';
    if (subSortField === 'subTaskNumber') { av = a.subTaskNumber; bv = b.subTaskNumber; }
    else if (subSortField === 'name') { av = a.name; bv = b.name; }
    else if (subSortField === 'task') { av = a.task.taskId; bv = b.task.taskId; }
    else if (subSortField === 'client') { av = a.task.client?.clientName || ''; bv = b.task.client?.clientName || ''; }
    else if (subSortField === 'assignedTo') { av = a.assignedTo?.staffName || ''; bv = b.assignedTo?.staffName || ''; }
    else if (subSortField === 'createdAt') { av = a.createdAt || ''; bv = b.createdAt || ''; }
    else if (subSortField === 'dueDate') { av = a.dueDate || ''; bv = b.dueDate || ''; }
    else if (subSortField === 'status') { av = a.status; bv = b.status; }
    return subSortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Sub-Tasks</h2>
        <span className="text-sm text-gray-400">{filtered.length} sub-task{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      <div className="card">
        <div className="flex flex-wrap gap-2 items-center mb-3">
          <input className="input-field w-56 text-sm" placeholder="Search by number, name, task, client…"
            value={search} onChange={(e) => setSearch(e.target.value)} />
          <select className="input-field w-40 text-sm" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="">All Statuses</option>
            <option value="OPEN">Open</option>
            <option value="SENT_FOR_REVIEW">Sent for Review</option>
            <option value="CLOSED">Closed</option>
          </select>
          <span className="text-xs text-gray-400 ml-auto">{filtered.length} sub-task{filtered.length !== 1 ? 's' : ''}</span>
        </div>

        {loading ? <p className="text-gray-500 text-sm">Loading…</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="table-header cursor-pointer select-none" onClick={() => handleSubSort('subTaskNumber')}><span className="font-semibold">Sub-Task #</span> <SubSI f="subTaskNumber" /></th>
                  <th className="table-header cursor-pointer select-none" onClick={() => handleSubSort('name')}><span className="font-semibold">Name</span> <SubSI f="name" /></th>
                  <th className="table-header cursor-pointer select-none" onClick={() => handleSubSort('task')}><span className="font-semibold">Parent Task</span> <SubSI f="task" /></th>
                  <th className="table-header">
                    <ColumnFilter label="Client" options={clientFilterOptions} value={filterClient} onChange={setFilterClient}
                      sortable onSort={() => handleSubSort('client')} sortIcon={<SubSI f="client" />} />
                  </th>
                  <th className="table-header">
                    <ColumnFilter label="Assigned To" options={assignedToOptions} value={filterAssignedTo} onChange={setFilterAssignedTo}
                      sortable onSort={() => handleSubSort('assignedTo')} sortIcon={<SubSI f="assignedTo" />} />
                  </th>
                  <th className="table-header w-24">
                    <DateRangeFilter label="Created"
                      fromValue={filterSubCreatedFrom} toValue={filterSubCreatedTo}
                      onChange={(f, t) => { setFilterSubCreatedFrom(f); setFilterSubCreatedTo(t); }}
                      sortable onSort={() => handleSubSort('createdAt')} sortIcon={<SubSI f="createdAt" />} />
                  </th>
                  <th className="table-header w-24">
                    <DateRangeFilter label="Due Date"
                      fromValue={filterSubDueFrom} toValue={filterSubDueTo}
                      onChange={(f, t) => { setFilterSubDueFrom(f); setFilterSubDueTo(t); }}
                      sortable onSort={() => handleSubSort('dueDate')} sortIcon={<SubSI f="dueDate" />} />
                  </th>
                  <th className="table-header">
                    <ColumnFilter label="Status"
                      options={[
                        { value: 'OPEN', label: 'Open' },
                        { value: 'SENT_FOR_REVIEW', label: 'Sent for Review' },
                        { value: 'CLOSED', label: 'Closed' },
                      ]}
                      value={filterStatus} onChange={setFilterStatus}
                      sortable onSort={() => handleSubSort('status')} sortIcon={<SubSI f="status" />} />
                  </th>
                  <th className="table-header"><span className="font-semibold">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {sortedSubs.map((st) => (
                  <tr key={st.id} className="hover:bg-gray-50">
                    <td className="table-cell font-mono text-xs font-medium text-blue-700">{st.subTaskNumber}</td>
                    <td className="table-cell font-medium max-w-[180px]">
                      <span className="block truncate" title={st.name}>{st.name}</span>
                      {st.description && (
                        <span className="block text-xs text-gray-400 truncate" title={st.description}>{st.description}</span>
                      )}
                    </td>
                    <td className="table-cell">
                      <span className="font-mono text-xs text-gray-600">{st.task.taskId}</span>
                      <span className="block text-xs text-gray-500 truncate max-w-[140px]" title={st.task.taskName}>{st.task.taskName}</span>
                    </td>
                    <td className="table-cell text-gray-500 text-xs" title={st.task.client?.clientName || ''}>{st.task.client?.clientName || '—'}</td>
                    <td className="table-cell text-gray-600 text-xs" title={st.assignedTo?.staffName || ''}>{st.assignedTo?.staffName || '—'}</td>
                    <td className="table-cell text-xs text-gray-500 w-24">
                      {st.createdAt ? format(new Date(st.createdAt), 'dd-MMM-yy') : '—'}
                    </td>
                    <td className="table-cell text-xs w-24">
                      {st.dueDate
                        ? <span className={new Date(st.dueDate) < new Date() && st.status !== 'CLOSED' ? 'text-red-600 font-medium' : 'text-gray-600'}>
                            {format(new Date(st.dueDate), 'dd-MMM-yy')}
                          </span>
                        : '—'}
                    </td>
                    <td className="table-cell">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusBadge(st.status)}`}>
                        {st.status.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="table-cell">
                      <div className="flex gap-2 items-center flex-wrap">
                        {st.status === 'OPEN' && (
                          <button className="text-xs text-amber-600 hover:text-amber-800 font-medium"
                            onClick={() => handleStatusChange(st, 'SENT_FOR_REVIEW')}>
                            Send for Review
                          </button>
                        )}
                        {st.status !== 'CLOSED' && canClose(st) && (
                          <button className="text-xs text-gray-600 hover:text-gray-900 font-medium"
                            onClick={() => handleStatusChange(st, 'CLOSED')}>
                            Close
                          </button>
                        )}
                        {st.status !== 'CLOSED' && (
                          <button className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                            onClick={() => openEdit(st)}>
                            Edit
                          </button>
                        )}
                        {canDelete(st) && (
                          <button className="text-xs text-red-500 hover:text-red-700"
                            onClick={() => handleDelete(st.id)}>
                            Del
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={9} className="table-cell text-center text-gray-400 py-8">No sub-tasks found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit modal */}
      {editingRow && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Edit Sub-Task
                <span className="ml-2 font-mono text-sm text-blue-600">{editingRow.subTaskNumber}</span>
              </h3>
              <button className="text-gray-400 hover:text-gray-600 text-xl" onClick={() => setEditingRow(null)}>✕</button>
            </div>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label className="label">Name <span className="text-red-500">*</span></label>
                <input className="input-field" value={editForm.name} required
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
              </div>
              <div>
                <label className="label">Description</label>
                <textarea className="input-field" rows={3} value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Assign To</label>
                  <select className="input-field" value={editForm.assignedToId}
                    onChange={(e) => setEditForm({ ...editForm, assignedToId: e.target.value })}>
                    <option value="">— None —</option>
                    {activeStaff.map((s) => <option key={s.id} value={s.id}>{s.staffName}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Due Date</label>
                  <input type="date" className="input-field" value={editForm.dueDate}
                    onChange={(e) => setEditForm({ ...editForm, dueDate: e.target.value })} />
                </div>
              </div>
              <div className="flex gap-3 justify-end pt-2">
                <button type="button" className="btn-secondary" onClick={() => setEditingRow(null)}>Cancel</button>
                <button type="submit" className="btn-primary">Update</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Page wrapper with tabs ───────────────────────────────────────────────────
const Tasks: React.FC = () => {
  const isMobile = useIsMobile();
  const [searchParams] = useSearchParams();
  const openTaskId = searchParams.get('openTask') ? Number(searchParams.get('openTask')) : undefined;
  const initialSubTab = (searchParams.get('subTab') as 'tasks' | 'subtasks' | 'litigations' | 'certificates' | 'compliances' | null) ?? 'tasks';
  const [pageTab, setPageTab] = useState<'tasks' | 'subtasks' | 'litigations' | 'certificates' | 'compliances'>(initialSubTab);
  // Optional deep-link to a specific inline tab on the auto-opened task
  // detail (e.g. ?openTask=42&detailTab=litigations from the firm-wide list).
  const ALLOWED_DETAIL_TABS = ['info','expenses','documents','subtasks','certificates','litigations','compliances','comments','history'] as const;
  type DetailTab = typeof ALLOWED_DETAIL_TABS[number];
  const rawDetailTab = searchParams.get('detailTab');
  const openDetailTab: DetailTab | undefined =
    rawDetailTab && (ALLOWED_DETAIL_TABS as readonly string[]).includes(rawDetailTab) ? (rawDetailTab as DetailTab) : undefined;

  // Below md the desktop table is unusable, so the phone screen takes over
  // wholesale. Placed after the hooks above so the hook order is stable when
  // the viewport crosses the breakpoint mid-session.
  if (isMobile) return <MobileTasks />;

  return (
    <div>
      {/* Page-level tabs */}
      <div className="flex gap-1 border-b border-gray-200 mb-6">
        <button
          onClick={() => setPageTab('tasks')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            pageTab === 'tasks' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          📋 Tasks
        </button>
        <button
          onClick={() => setPageTab('subtasks')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            pageTab === 'subtasks' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          🔖 Sub-Tasks
        </button>
        <button
          onClick={() => setPageTab('litigations')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            pageTab === 'litigations' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          ⚖ Litigations
        </button>
        <button
          onClick={() => setPageTab('certificates')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            pageTab === 'certificates' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          📋 Certificates
        </button>
        <button
          onClick={() => setPageTab('compliances')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            pageTab === 'compliances' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          📅 Compliances
        </button>
      </div>

      {pageTab === 'tasks' && <TasksContent openTaskId={openTaskId} openDetailTab={openDetailTab} />}
      {pageTab === 'subtasks' && <SubTasksList />}
      {pageTab === 'litigations' && <LitigationsList />}
      {pageTab === 'certificates' && <CertificatesPage />}
      {pageTab === 'compliances' && <CompliancesPage />}
    </div>
  );
};

export default Tasks;
